
import { useEffect, useMemo, useRef, useState } from 'react'
import React from 'react'
import { Container, Card, Row, Col, Button, Modal, Form, FormGroup, FormLabel, FormSelect, Alert } from 'react-bootstrap'
import { 
  FaArrowLeft, 
  FaEye, 
  FaUser, 
  FaPhone, 
  FaEnvelope, 
  FaMapMarkerAlt,
  FaIdCard,
  FaUsers,
  FaShieldAlt,
  FaWallet,
  FaCalendarCheck,
  FaGraduationCap,
  FaPrint,
  FaWhatsapp,
  FaVenusMars,
  FaBus,
  FaExclamationCircle,
} from 'react-icons/fa'
import { 
  BsPrinterFill,
  BsPencilFill,
  BsBuilding,
  BsCalendar3,
  BsPersonBadge,
  BsFileEarmarkText,
  BsArrowClockwise,
  BsChevronDown,
  BsChevronUp,
  BsFileEarmarkPdfFill,
  BsJournalBookmark,
} from 'react-icons/bs'
import { useNavigate, useParams, useLocation } from 'react-router-dom'
import ApiEndpoints, {BASE_URL} from './commonComponents/ApiEndpoints'
import Cookies from 'js-cookie'
import toast from 'react-hot-toast'
import axios from 'axios'
import { useSidebar } from './SidebarContext'
import { useAuth } from './AuthContext'
import ProfileDropdown from './commonComponents/ProfileDropdown'
import { useInstitute } from './commonComponents/InstituteContext'
import { LuGraduationCap } from 'react-icons/lu'
import jsPDF from 'jspdf'
import {
  getPickupAddress,
  isParentUserType,
  mapParentAssignedTransports,
  studentHasAssignedBus,
} from '../utils/parentTransport'

function blobToDataURL(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onloadend = () => resolve(reader.result)
    reader.onerror = reject
    reader.readAsDataURL(blob)
  })
}

async function fetchImageAsDataUrlForPdf(url) {
  if (!url || String(url).startsWith('data:')) return url

  try {
    const res = await fetch(url, { mode: 'cors', credentials: 'omit' })
    if (res.ok) {
      return await blobToDataURL(await res.blob())
    }
  } catch {
    // Direct cross-origin fetch failed; try proxies below.
  }

  try {
    const proxiedUrl = `https://images.weserv.nl/?url=${encodeURIComponent(url)}`
    const res = await fetch(proxiedUrl)
    if (res.ok) {
      return await blobToDataURL(await res.blob())
    }
  } catch {
    // Try one more proxy.
  }

  try {
    const proxiedUrl = `https://api.allorigins.win/raw?url=${encodeURIComponent(url)}`
    const res = await fetch(proxiedUrl)
    if (res.ok) {
      return await blobToDataURL(await res.blob())
    }
  } catch {
    // Ignore and fall through.
  }

  return null
}

async function makeCircularImageDataUrl(sourceDataUrl, size = 256) {
  if (!sourceDataUrl) return null

  try {
    return await new Promise((resolve, reject) => {
      const imageEl = new Image()
      imageEl.onload = () => {
        try {
          const canvas = document.createElement('canvas')
          canvas.width = size
          canvas.height = size
          const ctx = canvas.getContext('2d')
          const minSide = Math.min(imageEl.naturalWidth, imageEl.naturalHeight)
          const sx = (imageEl.naturalWidth - minSide) / 2
          const sy = (imageEl.naturalHeight - minSide) / 2

          ctx.save()
          ctx.beginPath()
          ctx.arc(size / 2, size / 2, size / 2, 0, Math.PI * 2)
          ctx.closePath()
          ctx.clip()
          ctx.drawImage(imageEl, sx, sy, minSide, minSide, 0, 0, size, size)
          ctx.restore()

          resolve(canvas.toDataURL('image/png'))
        } catch (e) {
          reject(e)
        }
      }
      imageEl.onerror = reject
      imageEl.src = sourceDataUrl
    })
  } catch {
    return sourceDataUrl
  }
}

async function makeWatermarkImageDataUrl(sourceDataUrl, size = 600, opacity = 0.08) {
  if (!sourceDataUrl) return null

  try {
    return await new Promise((resolve, reject) => {
      const imageEl = new Image()
      imageEl.onload = () => {
        try {
          const canvas = document.createElement('canvas')
          canvas.width = size
          canvas.height = size
          const ctx = canvas.getContext('2d')
          const minSide = Math.min(imageEl.naturalWidth, imageEl.naturalHeight)
          const sx = (imageEl.naturalWidth - minSide) / 2
          const sy = (imageEl.naturalHeight - minSide) / 2

          ctx.clearRect(0, 0, size, size)
          ctx.save()
          ctx.beginPath()
          ctx.arc(size / 2, size / 2, size / 2, 0, Math.PI * 2)
          ctx.closePath()
          ctx.clip()
          ctx.globalAlpha = opacity
          ctx.drawImage(imageEl, sx, sy, minSide, minSide, 0, 0, size, size)
          ctx.restore()

          resolve(canvas.toDataURL('image/png'))
        } catch (e) {
          reject(e)
        }
      }
      imageEl.onerror = reject
      imageEl.src = sourceDataUrl
    })
  } catch {
    return sourceDataUrl
  }
}

function resolveProfileCourseClassForNav(data, enrolledRows) {
  const rows = Array.isArray(enrolledRows) ? enrolledRows : []
  const nonempty = (v) =>
    v != null && String(v).trim() !== '' && String(v).toLowerCase() !== 'null'

  if (!data || typeof data !== 'object') {
    return { courseId: '', classId: '' }
  }

  const rawCourse = data.course_id
  const rawClass = data.class_id
  const hasCourse = nonempty(rawCourse)
  const hasClass = nonempty(rawClass)

  if (hasCourse && hasClass) {
    return { courseId: rawCourse, classId: rawClass }
  }

  if (rows.length === 0) {
    return { courseId: hasCourse ? rawCourse : '', classId: hasClass ? rawClass : '' }
  }

  if (hasCourse) {
    const match = rows.find((r) => String(r?.course_id ?? '') === String(rawCourse))
    const classId = hasClass ? rawClass : match?.class_id ?? rows[0]?.class_id
    return { courseId: rawCourse, classId }
  }

  const first = rows[0]
  return {
    courseId: first?.course_id ?? '',
    classId: first?.class_id ?? '',
  }
}

const ViewStudent = ({ student }) => {
  const { user, logout } = useAuth()
  const { isCollapsed } = useSidebar()
  const [marginLeft, setMarginLeft] = useState('0px')
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth <= 768 : false,
  )
  const [logoError, setLogoError] = useState(false)
  const { logo, name, id: instituteId } = useInstitute();
  const isParentUser = isParentUserType(user, Cookies.get('user_type'))

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 992) {
        const expandedWidth = 245
        const collapsedWidth = 85
        const gap = 1

        setMarginLeft(
          isCollapsed
            ? `${collapsedWidth + gap}px`
            : `${expandedWidth + gap}px`,
        )
      } else {
        setMarginLeft('0px')
      }

      setIsMobile(window.innerWidth <= 768)
    }

    handleResize()
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [isCollapsed])

  const { id } = useParams()
  console.log('id:', id)
  const [data, setData] = useState('')
  console.log('data is define here', data)
  const navigate = useNavigate()
  const location = useLocation()
  
  // Check if user came from PassedOutStudent route
  const fromPassedOutStudents = location.state?.fromPassedOutStudents || false

  const [studentData, setStudentData] = useState(null)
  const [courses, setCourses] = useState([])
  const [classes, setClasses] = useState([])
  const [institutes, setInstitutes] = useState([])
  const [parentEmail, setParentEmail]=useState("")
  const [opentDateModal, setOpenDateModal] = useState(false)
  const [highlightUpdate, setHighlightUpdate] = useState(false)
  const [leavingDate, setLeavingDate] = useState('')
  const [openPassedOutModal, setOpenPassedOutModal] = useState(false)
  const [passedOutDate, setPassedOutDate] = useState('')
  const [passedOutConfirmed, setPassedOutConfirmed] = useState(false)
  const [guardians, setGuardians] = useState([])
  const [loadingGuardians, setLoadingGuardians] = useState(false)
  
  // Section visibility — expanded on desktop, collapsed on mobile by default
  const getDefaultSectionOpen = () =>
    typeof window !== 'undefined' ? window.innerWidth > 768 : true
  const [showPersonalInfo, setShowPersonalInfo] = useState(getDefaultSectionOpen)
  const [showParentInfo, setShowParentInfo] = useState(getDefaultSectionOpen)
  const [showAssignGuardian, setShowAssignGuardian] = useState(getDefaultSectionOpen)
  const [showAssignTransport, setShowAssignTransport] = useState(getDefaultSectionOpen)
  const [transportForm, setTransportForm] = useState({
    busId: '',
    busSearch: '',
    pickup: '',
  })
  const [transportOptions, setTransportOptions] = useState([])
  const [showTransportDropdown, setShowTransportDropdown] = useState(false)
  const [assignedTransports, setAssignedTransports] = useState([])
  const [assignedTransportError, setAssignedTransportError] = useState('')
  const [assigningTransport, setAssigningTransport] = useState(false)
  const [removingTransport, setRemovingTransport] = useState(false)
  const [loadingTransportRefresh, setLoadingTransportRefresh] = useState(false)
  const [loadingAssignedTransportSync, setLoadingAssignedTransportSync] = useState(false)
  const transportAssignmentSyncId = useRef(0)
  const lastTransportPickupRef = useRef('')
  const [showStudentActions, setShowStudentActions] = useState(getDefaultSectionOpen)
  const [enrolledCourseRows, setEnrolledCourseRows] = useState([])

  const SHARED_COOKIE_OPTIONS = { expires: 7, path: '/', sameSite: 'Lax' }

  const parseCookieJson = (value, fallback = null) => {
    if (!value) return fallback
    try {
      return JSON.parse(value)
    } catch (e) {
      return fallback
    }
  }

  const getValidInstituteId = (...candidates) => {
    for (const candidate of candidates) {
      if (candidate === undefined || candidate === null || candidate === '') continue
      const numericCandidate = Number(String(candidate).trim())
      if (Number.isFinite(numericCandidate) && numericCandidate > 0) {
        return String(numericCandidate)
      }
    }
    return null
  }

  const setCookieIfChanged = (key, value, options = SHARED_COOKIE_OPTIONS) => {
    if (value === undefined || value === null || value === '') return
    const normalizedValue =
      typeof value === 'string' ? value : JSON.stringify(value)

    if (Cookies.get(key) !== normalizedValue) {
      if (options) {
        Cookies.set(key, normalizedValue, options)
      } else {
        Cookies.set(key, normalizedValue)
      }
    }
  }

  const syncRequiredCookiesForGuardianTab = () => {
    const userFromCookie = parseCookieJson(Cookies.get('user'))
    const activeUser = user || userFromCookie

    const resolvedInstituteId = getValidInstituteId(
      instituteId,
      activeUser?.institute_id,
      data?.institute_id,
      Cookies.get('institute_id'),
    )
    const resolvedInstituteLogo =
      Cookies.get('institute_logo') || (logo && logo !== 'no_image.png' ? logo : '')
    const resolvedInstituteName = name || Cookies.get('name') || Cookies.get('institute_name')
    const resolvedUserType = activeUser?.user_type || Cookies.get('user_type')
    const resolvedRoles =
      activeUser?.roles ?? parseCookieJson(Cookies.get('roles'), Cookies.get('roles'))

    setCookieIfChanged('institute_id', resolvedInstituteId)
    setCookieIfChanged('institute_logo', resolvedInstituteLogo)
    setCookieIfChanged('name', resolvedInstituteName)
    setCookieIfChanged('institute_name', resolvedInstituteName)
    setCookieIfChanged('user_type', resolvedUserType)
    setCookieIfChanged('roles', resolvedRoles)

    if (activeUser?.token) {
      const userPayload = resolvedInstituteId
        ? { ...activeUser, institute_id: Number(resolvedInstituteId) }
        : activeUser
      setCookieIfChanged('user', userPayload, SHARED_COOKIE_OPTIONS)
    }

    return { resolvedInstituteId }
  }

  const handleOpenAllGuardiansInNewTab = async () => {
    const newTab = window.open('', '_blank')
    if (!newTab) {
      toast.error('Please allow popups to open View All Guardians.')
      return
    }

    const { resolvedInstituteId } = syncRequiredCookiesForGuardianTab()
    const now = Date.now().toString()

    // New tab has isolated sessionStorage, so seed it before route bootstrap runs.
    try {
      sessionStorage.setItem('institute_confirmed', 'true')
      sessionStorage.setItem('institute_confirmed_timestamp', now)
      newTab.sessionStorage.setItem('institute_confirmed', 'true')
      newTab.sessionStorage.setItem('institute_confirmed_timestamp', now)
    } catch (e) {
      // Continue with navigation even if browser blocks storage writes.
    }

    await Promise.resolve()
    const targetUrl = resolvedInstituteId
      ? `/AddGuardian?institute_id=${encodeURIComponent(resolvedInstituteId)}`
      : '/AddGuardian'
    newTab.location.href = targetUrl
  }

  useEffect(() => {
    const storedInstitute = Cookies.get('institute_name')

    if (storedInstitute) {
      setInstitutes([{ name: storedInstitute }])
    }
  }, [])
  useEffect(() => {
    ApiEndpoints.getAllCourses().then((res) => setCourses(res.data.data))
    ApiEndpoints.getAllClasses().then((res) => setClasses(res.data.data))
  }, [])
  useEffect(() => {
    ApiEndpoints.getAllInstitutes().then((res) => {
      if (Array.isArray(res.data.data)) {
        setInstitutes(res.data.data)
      } else {
        setInstitutes([])
      }
    })
  }, [])

  const [img, setImg] = useState(null)
  useEffect(() => {
    const fetchStudentData = async () => {
      try {
        const response = await ApiEndpoints.viewProfile(id)
        console.log('Response of viewProfile: ', response.data.data)
        setData(response.data.data)
        setStudentData(response.data.data)
        setImg(response.data.data)
      } catch (error) {
        error(error.response?.data?.message || 'Something wents worng.')
      }
    }
    if (id) fetchStudentData()
  }, [id])

  // Fetch guardians for the student
  const fetchGuardians = async () => {
    if (!id) return
    setLoadingGuardians(true)
    try {
      const userCookie = Cookies.get("user")
      const user = JSON.parse(userCookie)
      let token = user.token

      // Try multiple endpoint patterns based on API structure
      const endpoints = [
        `/students/${id}/parents`,
        `/parents/student/${id}`,
        `/students/${id}/guardians`,
      ]

      let success = false
      for (const endpoint of endpoints) {
        try {
          const response = await axios.get(
            BASE_URL + endpoint,
            {
              headers: {
                "Accept": "application/json",
                "Authorization": `Bearer ${token}`,
              },
            }
          )

          if (response.data && (response.data.status || response.data.data)) {
            const guardiansData = response.data.data || response.data
            if (Array.isArray(guardiansData)) {
              setGuardians(guardiansData)
            } else if (guardiansData && typeof guardiansData === 'object') {
              // If data is an object, try to extract array from it
              const dataArray = guardiansData.guardians || guardiansData.parents || guardiansData.data || []
              setGuardians(Array.isArray(dataArray) ? dataArray : [])
            } else {
              setGuardians([])
            }
            success = true
            break
          }
        } catch (endpointError) {
          // Continue to next endpoint
          continue
        }
      }

      if (!success) {
        setGuardians([])
      }
    } catch (error) {
      console.error('Error fetching guardians:', error)
      setGuardians([])
    } finally {
      setLoadingGuardians(false)
    }
  }

  useEffect(() => {
    if (id && data) {
      fetchGuardians()
    }
  }, [id, data])
  const calculateAge = (dob) => {
    if (!dob) return ''
    const birthDate = new Date(dob)
    const today = new Date()
    let age = today.getFullYear() - birthDate.getFullYear()
    const monthDiff = today.getMonth() - birthDate.getMonth()

    if (
      monthDiff < 0 ||
      (monthDiff === 0 && today.getDate() < birthDate.getDate())
    ) {
      age--
    }
    return age
  }

  const handleBack = () => {
    navigate('/students')
  }

  const handleIdCard = () => {
    const sourceState = location.state?.fromPassedOutStudents 
      ? { fromPassedOutStudents: true }
      : { fromActiveStudents: true }
    navigate(`/student/id-card/${id}`, { state: sourceState })
  }

  const handleSaveAsPDF = async () => {
    if (!data) {
      toast.error('Student data not available')
      return
    }

    let studentImageData = null
    let instituteLogoData = null
    let instituteWatermarkData = null
    const defaultStudentPhoto = `${process.env.PUBLIC_URL || ''}/studentprofile.jpg`
    const rawStudentImg = data?.student_image || img?.student_image
    const imageUrl =
      rawStudentImg &&
      String(rawStudentImg).trim() !== '' &&
      String(rawStudentImg).toLowerCase() !== 'null'
        ? rawStudentImg
        : defaultStudentPhoto
    if (imageUrl) {
      try {
        studentImageData = await fetchImageAsDataUrlForPdf(imageUrl)
      } catch {
        studentImageData = null
      }
    }

    const instituteLogoUrl =
      logo &&
      String(logo).trim() !== '' &&
      String(logo).toLowerCase() !== 'no_image.png'
        ? logo
        : null

    if (instituteLogoUrl) {
      try {
        const instituteLogoRawData = await fetchImageAsDataUrlForPdf(instituteLogoUrl)
        instituteLogoData = await makeCircularImageDataUrl(instituteLogoRawData)
        instituteWatermarkData = await makeWatermarkImageDataUrl(instituteLogoRawData)
      } catch {
        instituteLogoData = null
        instituteWatermarkData = null
      }
    }

    try {
      const doc = new jsPDF('p', 'mm', 'a4')
      const pageWidth = doc.internal.pageSize.getWidth()
      const pageHeight = doc.internal.pageSize.getHeight()
      const margin = 15
      let yPos = margin
      const drawCenterWatermark = () => {
        if (!instituteWatermarkData) return
        const watermarkSizeMm = 95
        const x = (pageWidth - watermarkSizeMm) / 2
        const y = (pageHeight - watermarkSizeMm) / 2
        doc.addImage(instituteWatermarkData, 'PNG', x, y, watermarkSizeMm, watermarkSizeMm)
      }

      drawCenterWatermark()

      // Helper function to add a new page if needed
      const checkPageBreak = (requiredHeight) => {
        if (yPos + requiredHeight > pageHeight - margin) {
          doc.addPage()
          yPos = margin
          drawCenterWatermark()
        }
      }

      const instituteDisplayName = String(
        name || Cookies.get('name') || Cookies.get('institute_name') || '',
      ).trim()

      if (instituteLogoData || instituteDisplayName) {
        const topHeaderStartY = yPos
        const logoSizeMm = 16
        let consumedHeaderHeight = 0

        if (instituteLogoData) {
          doc.addImage(instituteLogoData, 'JPEG', margin, topHeaderStartY, logoSizeMm, logoSizeMm)
          consumedHeaderHeight = logoSizeMm
        }

        if (instituteDisplayName) {
          const nameMaxWidth = pageWidth - margin * 2
          doc.setFontSize(13)
          doc.setFont('helvetica', 'bold')
          doc.setTextColor(34, 56, 67)
          const instituteNameLines = doc.splitTextToSize(instituteDisplayName, nameMaxWidth)
          doc.text(instituteNameLines, pageWidth / 2, topHeaderStartY + 6, { align: 'center' })
          consumedHeaderHeight = Math.max(consumedHeaderHeight, instituteNameLines.length * 5 + 2)
        }

        yPos = topHeaderStartY + consumedHeaderHeight + 2
      } else {
        yPos += 10
      }
    
      // Title
      doc.setFontSize(20)
      doc.setFont('helvetica', 'bold')
      doc.setTextColor(34, 56, 67) // #223843
      doc.text('Admission Form', pageWidth / 2, yPos, { align: 'center' })
      yPos += 6
      doc.setDrawColor(209, 213, 219)
      doc.line(margin, yPos, pageWidth - margin, yPos)
      yPos += 5

      // Student Name & Admission Number (left); photo (right). Text vertically centered to photo when image exists.
      const headerRowTopY = yPos
      const headerPhotoSizeMm = 28
      const headerPhotoX = pageWidth - margin - headerPhotoSizeMm
      const headerTextMaxWidth = pageWidth - margin * 2 - headerPhotoSizeMm - 4

      doc.setFontSize(18)
      doc.setFont('helvetica', 'bold')
      doc.setTextColor(17, 24, 39) // Dark gray
      const studentName = data?.name
        ? data.name.charAt(0).toUpperCase() + data.name.slice(1).toLowerCase()
        : 'N/A'
      const nameLines = doc.splitTextToSize(studentName, headerTextMaxWidth)
      const nameLinesHeight = nameLines.length * 7
      // Center text block vertically within photo height (photo top = headerRowTopY); derived from visual mid alignment.
      const nameBlockTopY = studentImageData
        ? Math.max(headerRowTopY, headerRowTopY + (27 - nameLinesHeight) / 2)
        : headerRowTopY

      doc.text(nameLines, margin, nameBlockTopY)

      doc.setFontSize(12)
      doc.setFont('helvetica', 'normal')
      doc.setTextColor(107, 114, 128) // Gray
      const admissionLineY = nameBlockTopY + nameLinesHeight + 2
      doc.text(`Admission Number: ${data?.admission_number || 'N/A'}`, margin, admissionLineY)

      if (studentImageData) {
        const headerPhotoY = headerRowTopY
        doc.addImage(studentImageData, 'JPEG', headerPhotoX, headerPhotoY, headerPhotoSizeMm, headerPhotoSizeMm)
      }

      const admissionLineBottom = admissionLineY + 5
      const headerPhotoBottom = studentImageData ? headerRowTopY + headerPhotoSizeMm : nameBlockTopY
      yPos = Math.max(admissionLineBottom, headerPhotoBottom) + 6

      // Divider line
      doc.setDrawColor(209, 213, 219)
      doc.line(margin, yPos, pageWidth - margin, yPos)
      yPos += 8

      // Section: Personal Information
      doc.setFontSize(14)
      doc.setFont('helvetica', 'bold')
      doc.setTextColor(34, 56, 67) // #223843
      doc.text('Personal Information', margin, yPos)
      yPos += 8

      doc.setFontSize(10)
      doc.setFont('helvetica', 'normal')
      doc.setTextColor(17, 24, 39)

      // Admission Details
      doc.setFont('helvetica', 'bold')
      doc.text('Admission Details:', margin, yPos)
      yPos += 6
      doc.setFont('helvetica', 'normal')
      
      const admissionDetails = [
        `Admission Number: ${data?.admission_number || 'N/A'}`,
        `Admission Date: ${data?.admission_date || 'N/A'}`,
        `Institute Name: ${instituteName}`
      ]
      
      admissionDetails.forEach((detail) => {
        checkPageBreak(6)
        doc.text(detail, margin + 5, yPos)
        yPos += 6
      })
      yPos += 3

      // Personal Details
      checkPageBreak(20)
      doc.setFont('helvetica', 'bold')
      doc.text('Personal Details:', margin, yPos)
      yPos += 6
      doc.setFont('helvetica', 'normal')
      
      const personalDetails = [
        `Birth Date: ${data?.dob || 'N/A'}`,
        `Age: ${calculateAge(data?.dob) || 'N/A'} years`,
        `Gender: ${data?.gender || 'N/A'}`
      ]
      
      personalDetails.forEach((detail) => {
        checkPageBreak(6)
        doc.text(detail, margin + 5, yPos)
        yPos += 6
      })
      yPos += 3

      // Contact Information
      checkPageBreak(25)
      doc.setFont('helvetica', 'bold')
      doc.text('Contact Information:', margin, yPos)
      yPos += 6
      doc.setFont('helvetica', 'normal')
      
      const contactDetails = [
        `Email Address: ${data?.email || 'N/A'}`,
        `Phone Number: ${data?.phone || 'N/A'}`,
        `Parent's Number: ${data?.parents_number || 'N/A'}`,
        `WhatsApp Number: ${data?.whatsapp_number || 'N/A'}`,
        `Address: ${data?.address || 'N/A'}`
      ]
      
      contactDetails.forEach((detail) => {
        checkPageBreak(6)
        doc.text(detail, margin + 5, yPos)
        yPos += 6
      })
      yPos += 3

      // Additional Information
      checkPageBreak(10)
      doc.setFont('helvetica', 'bold')
      doc.text('Additional Information:', margin, yPos)
      yPos += 6
      doc.setFont('helvetica', 'normal')
      doc.text(`ID Proof Number: ${data?.id_proof_number || 'N/A'}`, margin + 5, yPos)
      yPos += 10

      // Section: Parent Information
      checkPageBreak(20)
      doc.setDrawColor(209, 213, 219)
      doc.line(margin, yPos, pageWidth - margin, yPos)
      yPos += 8

      doc.setFontSize(14)
      doc.setFont('helvetica', 'bold')
      doc.setTextColor(34, 56, 67) // #223843
      doc.text('Parent Information', margin, yPos)
      yPos += 8

      doc.setFontSize(10)
      doc.setFont('helvetica', 'normal')
      doc.setTextColor(17, 24, 39)
      
      const parentDetails = [
        `Father's Name: ${data?.father_name || 'N/A'}`,
        `Mother's Name: ${data?.mother_name || 'N/A'}`
      ]
      
      parentDetails.forEach((detail) => {
        checkPageBreak(6)
        doc.text(detail, margin + 5, yPos)
        yPos += 6
      })
      yPos += 10

      // Section: Course & Class Information
      checkPageBreak(20)
      doc.setDrawColor(209, 213, 219)
      doc.line(margin, yPos, pageWidth - margin, yPos)
      yPos += 8

      doc.setFontSize(14)
      doc.setFont('helvetica', 'bold')
      doc.setTextColor(34, 56, 67) // #223843
      doc.text('Academic Information', margin, yPos)
      yPos += 8

      doc.setFontSize(10)
      doc.setFont('helvetica', 'normal')
      doc.setTextColor(17, 24, 39)
      
      const academicDetails = [
        `Course: ${courseName}`,
        `Class: ${className}`,
        `Status: ${data?.status ? data.status.charAt(0).toUpperCase() + data.status.slice(1) : 'N/A'}`
      ]
      
      academicDetails.forEach((detail) => {
        checkPageBreak(6)
        doc.text(detail, margin + 5, yPos)
        yPos += 6
      })

      // Footer: signature labels (left / right) and generated date
      const totalPages = doc.internal.pages.length - 1
      const footerSigY = pageHeight - 16
      const footerDateY = pageHeight - 8
      for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i)
        doc.setFontSize(8)
        doc.setTextColor(107, 114, 128)
        doc.setFont('helvetica', 'bold')
        doc.text('Signature of Guardian/Parent', margin, footerSigY, { align: 'left' })
        doc.text('Signature of Student', pageWidth - margin, footerSigY, { align: 'right' })
        doc.setFont('helvetica', 'italic')
        doc.text(
          `Generated on: ${new Date().toLocaleDateString()} | Page ${i} of ${totalPages}`,
          pageWidth / 2,
          footerDateY,
          { align: 'center' }
        )
      }

      // Save the PDF
      const fileName = `Student_Profile_${data?.admission_number || data?.name || 'Student'}_${new Date().getTime()}.pdf`
      doc.save(fileName)
      toast.success('PDF generated successfully!')
    } catch (error) {
      console.error('Error generating PDF:', error)
      toast.error('Failed to generate PDF. Please try again.')
    }
  }
  const handleUpdateStudentProfile = () => {
    navigate(`/student/update-profile/${id}`)
  }
  const handleViewFees = (id, course_id, class_id) => {
    navigate(`/student/fees/${id}`, { state: { fromStudentProfile: true } })
    console.log('Student ID:', id)
    console.log('Course ID:', course_id)
    console.log('Class ID:', class_id)
  }

  const isValidEmail = (email)=>{
    return /\S+@\S+\.\S+/.test(email);
  }

  const handleAssignParent = async (id) => {
    if(!parentEmail){
      toast.error("Please enter an email")
      return
    }

  if(!isValidEmail(parentEmail)){
    toast.error("Please enter a valid email")
    return
  }
  try {
    const userCookie = Cookies.get("user")
    const user = JSON.parse(userCookie)
    let token = user.token

    
    const response = await axios.post(
      BASE_URL+"/guardian/assign/student",
      {
        email: parentEmail,
        student_id: id,
      },
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      }
    )

    toast.success(response.data.message)
    setParentEmail("")
    // Refresh guardians list after assigning
    fetchGuardians()
  } catch (error) {
    toast.error(error.response?.data?.message || "Something went wrong")
  }
}

  const findBusInList = (buses, busId) => {
    if (!Array.isArray(buses) || busId === undefined || busId === null) return null
    const s = String(busId)
    return (
      buses.find(
        (bus) =>
          String(bus?.bus_id) === s ||
          String(bus?.id) === s,
      ) || null
    )
  }

  const refreshStudentTransportAssignment = async (profileOverride = null) => {
    const studentId = Number(id)
    if (!Number.isFinite(studentId) || studentId <= 0) {
      lastTransportPickupRef.current = ''
      setAssignedTransports([])
      setAssignedTransportError('')
      return false
    }

    const profile =
      profileOverride && typeof profileOverride === 'object'
        ? profileOverride
        : studentData && typeof studentData === 'object'
          ? studentData
          : data && typeof data === 'object'
            ? data
            : null

    const parentViewer = isParentUserType(user, Cookies.get('user_type'))
    if (parentViewer) {
      if (studentHasAssignedBus(profile) === false) {
        lastTransportPickupRef.current = ''
        setAssignedTransports([])
        setAssignedTransportError('')
        return false
      }

      const syncId = ++transportAssignmentSyncId.current
      setLoadingAssignedTransportSync(true)
      try {
        const response = await ApiEndpoints.getChildBus()
        if (syncId !== transportAssignmentSyncId.current) return false
        const mapped = mapParentAssignedTransports(response?.data, studentId, profile)
        setAssignedTransports(mapped)
        setAssignedTransportError('')
        return true
      } catch (err) {
        if (syncId !== transportAssignmentSyncId.current) return false
        setAssignedTransports([])
        setAssignedTransportError(
          err.response?.data?.message ||
            (typeof err.response?.data === 'string' ? err.response.data : '') ||
            err.message ||
            'Unable to load assigned transport.',
        )
        return false
      } finally {
        if (syncId === transportAssignmentSyncId.current) {
          setLoadingAssignedTransportSync(false)
        }
      }
    }

    if (!profile?.id || Number(profile.id) !== studentId) {
      return false
    }

    const busId = Number(profile.assign_bus)
    if (!Number.isFinite(busId) || busId <= 0) {
      lastTransportPickupRef.current = ''
      setAssignedTransports([])
      setAssignedTransportError('')
      return false
    }

    const syncId = ++transportAssignmentSyncId.current

    setLoadingAssignedTransportSync(true)
    try {
      const userCookie = Cookies.get('user')
      const parsedUser = userCookie ? JSON.parse(userCookie) : null
      const token = parsedUser?.token
      const resolvedInstituteId = getValidInstituteId(
        instituteId,
        parsedUser?.institute_id,
        profile?.institute_id,
        Cookies.get('institute_id'),
      )

      const headers = {
        Accept: 'application/json',
        ...(resolvedInstituteId
          ? {
              'Institute-Id': Number(resolvedInstituteId),
              institute_id: Number(resolvedInstituteId),
            }
          : {}),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      }

      let studentsOnBus = []
      let studentsFetchOk = false
      let apiTransportError = ''

      try {
        const response = await axios.get(`${BASE_URL}/transport/${busId}/students`, {
          headers,
        })
        const payload = response?.data
        if (payload && payload.status === false) {
          apiTransportError =
            (typeof payload.message === 'string' && payload.message.trim()) ||
            'Transport not found'
        } else {
          studentsOnBus = Array.isArray(payload?.data) ? payload.data : []
          studentsFetchOk = true
        }
      } catch (err) {
        studentsOnBus = []
        studentsFetchOk = false
        apiTransportError =
          err.response?.data?.message ||
          (typeof err.response?.data === 'string' ? err.response.data : '') ||
          err.message ||
          'Unable to load assigned transport.'
      }

      if (syncId !== transportAssignmentSyncId.current) return false

      if (apiTransportError) {
        setAssignedTransports([])
        setAssignedTransportError(apiTransportError)
        return false
      }

      const studentOnBusRecord = studentsOnBus.find((s) => Number(s.id) === studentId)
      const studentOnBus = Boolean(studentOnBusRecord)

      let busMeta = findBusInList(transportOptions, busId)
      if (!busMeta) {
        const freshBuses = await fetchTransportOptions()
        if (syncId !== transportAssignmentSyncId.current) return false
        busMeta = findBusInList(freshBuses, busId)
      }

      if (studentsFetchOk && studentsOnBus.length > 0 && !studentOnBus) {
        lastTransportPickupRef.current = ''
        setAssignedTransports([])
        setAssignedTransportError('')
        toast.error('This student is not listed on the assigned transport.')
        return false
      }

      const pickup =
        getPickupAddress(studentOnBusRecord, profile) || lastTransportPickupRef.current || ''
      if (pickup) lastTransportPickupRef.current = pickup

      setAssignedTransports([
        {
          id: `assigned-bus-${busId}`,
          bus_id: String(busId),
          bus_number: busMeta?.bus_number || '',
          driver_name: busMeta?.driver_name || '',
          driver_contact_number: busMeta?.driver_contact_number || '',
          pickup,
        },
      ])
      setAssignedTransportError('')
      return true
    } finally {
      if (syncId === transportAssignmentSyncId.current) {
        setLoadingAssignedTransportSync(false)
      }
    }
  }

  const handleAssignTransport = async () => {
    const busId = transportForm.busId?.trim()
    const pickup = transportForm.pickup?.trim() || ''
    const studentId = Number(id)

    if (!busId) {
      toast.error('Please select a valid transport from the list.')
      return
    }

    if (!Number.isFinite(studentId) || studentId <= 0) {
      toast.error('Invalid student ID.')
      return
    }

    try {
      setAssigningTransport(true)
      const userCookie = Cookies.get('user')
      const parsedUser = userCookie ? JSON.parse(userCookie) : null
      const token = parsedUser?.token

      const payload = {
        bus_id: busId,
        student_id: studentId,
      }
      if (pickup) payload.pickup = pickup

      const response = await axios.post(
        'https://schoolapi.virtualprachar.com/api/transport/assign-bus',
        payload,
        token
          ? {
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
              },
            }
          : {
              headers: {
                'Content-Type': 'application/json',
              },
            },
      )

      lastTransportPickupRef.current = pickup
      setTransportForm({ busId: '', busSearch: '', pickup: '' })
      toast.success(response?.data?.message || 'Transport assigned successfully.')

      const busMeta = findBusInList(transportOptions, busId)
      setAssignedTransports([
        {
          id: `assigned-bus-${busId}`,
          bus_id: String(busId),
          bus_number: busMeta?.bus_number || '',
          driver_name: busMeta?.driver_name || '',
          driver_contact_number: busMeta?.driver_contact_number || '',
          pickup,
        },
      ])
      setAssignedTransportError('')

      try {
        const profileRes = await ApiEndpoints.viewProfile(id)
        const nextProfile = profileRes?.data?.data
        if (nextProfile) {
          const profileWithPickup = pickup
            ? {
                ...nextProfile,
                pickup: getPickupAddress(nextProfile) || pickup,
              }
            : nextProfile
          setData(profileWithPickup)
          setStudentData(profileWithPickup)
          setImg(profileWithPickup)
          await refreshStudentTransportAssignment(profileWithPickup)
        }
      } catch {
        // Profile refresh failed; assigned list already shows the latest pickup.
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to assign transport.')
    } finally {
      setAssigningTransport(false)
    }
  }

  const handleUnassignTransport = async () => {
    const studentId = Number(id)

    if (!Number.isFinite(studentId) || studentId <= 0) {
      toast.error('Invalid student ID.')
      return
    }

    try {
      setRemovingTransport(true)
      const userCookie = Cookies.get('user')
      const parsedUser = userCookie ? JSON.parse(userCookie) : null
      const token = parsedUser?.token

      const response = await axios.post(
        'https://schoolapi.virtualprachar.com/api/transport/assign-bus',
        {
          student_id: studentId,
          bus_id: null,
          pickup: '',
        },
        token
          ? {
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
              },
            }
          : {
              headers: {
                'Content-Type': 'application/json',
              },
            },
      )

      lastTransportPickupRef.current = ''
      setAssignedTransports([])
      setAssignedTransportError('')
      toast.success(response?.data?.message || 'Bus unassigned successfully')

      const clearAssignment = (prev) =>
        prev && typeof prev === 'object'
          ? { ...prev, assign_bus: null, pickup: null }
          : prev
      setData(clearAssignment)
      setStudentData(clearAssignment)
      setImg(clearAssignment)
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to unassign transport.')
    } finally {
      setRemovingTransport(false)
    }
  }

  const fetchTransportOptions = async () => {
    if (!id) return []
    if (isParentUserType(user, Cookies.get('user_type'))) return []
    try {
      const userCookie = Cookies.get('user')
      const parsedUser = userCookie ? JSON.parse(userCookie) : null
      const token = parsedUser?.token
      const resolvedInstituteId = getValidInstituteId(
        instituteId,
        parsedUser?.institute_id,
        data?.institute_id,
        Cookies.get('institute_id'),
      )

      if (!resolvedInstituteId) {
        setTransportOptions([])
        return []
      }
      const response = await axios.get(
        'https://schoolapi.virtualprachar.com/api/transport/all',
        {
          params: {
            institute_id: Number(resolvedInstituteId),
            instituteId: Number(resolvedInstituteId),
          },
          headers: {
            Accept: 'application/json',
            'Institute-Id': Number(resolvedInstituteId),
            institute_id: Number(resolvedInstituteId),
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        },
      )
      const buses = Array.isArray(response?.data?.data)
        ? response.data.data
        : Array.isArray(response?.data)
          ? response.data
          : []
      setTransportOptions(buses)
      return buses
    } catch (error) {
      setTransportOptions([])
      toast.error(error.response?.data?.message || 'Failed to load transports.')
      return []
    }
  }

  const refreshTransportList = async () => {
    setLoadingTransportRefresh(true)
    try {
      const parentViewer = isParentUserType(user, Cookies.get('user_type'))
      if (!parentViewer) {
        await fetchTransportOptions()
      }
      const ok = await refreshStudentTransportAssignment()
      if (ok) toast.success('Transport list refreshed.')
    } finally {
      setLoadingTransportRefresh(false)
    }
  }

  useEffect(() => {
    if (id && !isParentUserType(user, Cookies.get('user_type'))) {
      fetchTransportOptions()
    }
  }, [id, instituteId, data?.institute_id])

  useEffect(() => {
    if (!id) {
      lastTransportPickupRef.current = ''
      setAssignedTransports([])
      setAssignedTransportError('')
      return
    }

    const profile =
      studentData && typeof studentData === 'object'
        ? studentData
        : data && typeof data === 'object'
          ? data
          : null

    if (isParentUserType(user, Cookies.get('user_type'))) {
      refreshStudentTransportAssignment()
      return
    }

    if (!profile?.id || Number(profile.id) !== Number(id)) return

    refreshStudentTransportAssignment()
  }, [id, studentData?.assign_bus, data?.assign_bus, studentData?.id, data?.id, instituteId])

const handleViewParent = (id)=>{
  navigate(`/AddGuardian/${id.student_id}/${id.course_id}/${id.class_id}`)
    console.log('IDs:', id)
    console.log('Student ID:', id.student_id)
    console.log('Course ID:', id.course_id)
    console.log('Class ID:', id.class_id)
}

  const handleViewCourse = () => {
    const studentName = studentData?.name
    const admissionNumber = studentData?.admission_number

    navigate(`/student/enrolled-courses/${id}`, {
      state: {
        student_id: id,
        student_name: studentName,
        admission_number: admissionNumber,
      },
    })
    console.log('EnrolledCourses', id)
    console.log('studentName', studentName)
  }
  const handleViewAttendance = (id, course_id, class_id) => {
    if (!course_id || !class_id) {
      toast.error('Please add course and class first.')
      setHighlightUpdate(true)
      setTimeout(() => setHighlightUpdate(false), 5000)
    } else {
      navigate(`/student/view-attendance/${id}/${course_id}/${class_id}`)
      console.log('Student ID:', id)
      console.log('Course ID:', course_id)
      console.log('Class ID:', class_id)
    }
  }
  const handleViewCertificate = () => {
    navigate(`/student/${id}/certificates`)
  }
  const handleViewMarksheet = () => {
    navigate(`/student/${id}/marksheets`)
  }
  const handleViewTransferCertificate = () => {
    navigate(`/student/${id}/transfer-certificate`)
  }

  const handleViewHomework = (studentId) => {
    navigate(`/student/profile/${studentId}/homework`, {
      state: { fromStudentProfile: true },
    })
  }
  const fetchEnrolledCoursesForNav = async () => {
    if (!id) return
    try {
      const userCookie = Cookies.get('user')
      let token = ''
      if (userCookie) {
        const user = JSON.parse(userCookie)
        token = user?.token || ''
      }

      const response = await axios.get(BASE_URL + '/get/student/all/enroll', {
        params: { student_id: id },
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      })

      const enrolledData = Array.isArray(response?.data?.data) ? response.data.data : []
      setEnrolledCourseRows(enrolledData)
    } catch (error) {
      setEnrolledCourseRows([])
      toast.error(error?.response?.data?.message || 'Failed to fetch enrolled courses.')
    }
  }

  useEffect(() => {
    if (!id) return
    if (!data || typeof data !== 'object') return
    const nonempty = (v) =>
      v != null && String(v).trim() !== '' && String(v).toLowerCase() !== 'null'
    if (nonempty(data.course_id) && nonempty(data.class_id)) return
    fetchEnrolledCoursesForNav()
    // eslint-disable-next-line react-hooks/exhaustive-deps -- load enroll rows when profile omits course/class (e.g. passed-out); fetch is stable enough for this trigger
  }, [id, data?.course_id, data?.class_id])

  const { courseId: profileNavCourseId, classId: profileNavClassId } = useMemo(
    () => resolveProfileCourseClassForNav(data, enrolledCourseRows),
    [data, enrolledCourseRows],
  )

  const handleViewAdmitCard = () => {
    navigate(`/student/${id}/admit-card`)
  }
  const handleDischarge = () => {
    setOpenDateModal(true)
  }

  const handleDischargeNow = async (e) => {
    e.preventDefault()
    if (!leavingDate) {
      toast.error('Please select a leaving date.')
      return
    }
    const userCookie = Cookies.get('user')
    if (!userCookie) {
      toast.error('User not authenticated. Please log in.')
      return
    }

    try {
      const status = 'inactive'

      const user = JSON.parse(userCookie)
      let token = user.token

      const response = await axios.post(
        BASE_URL+`/students/${id}/status?status=${status}&leaving_date=${leavingDate}`,
        {},
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        },
      )
      toast.success(response.data.message)
      navigate('/student/discharged')
    } catch (error) {
      console.error(error.response?.data?.message)
    }
  }

  const resetPassedOutModal = () => {
    setOpenPassedOutModal(false)
    setPassedOutDate('')
    setPassedOutConfirmed(false)
  }

  const handlePassedOut = () => {
    setPassedOutDate('')
    setPassedOutConfirmed(false)
    setOpenPassedOutModal(true)
  }

  const handlePassedOutNow = async (e) => {
    e.preventDefault()
    if (!passedOutDate) {
      toast.error('Please select a passed out date.')
      return
    }
    if (!passedOutConfirmed) {
      toast.error('Please confirm that you understand this action.')
      return
    }
    const userCookie = Cookies.get('user')
    if (!userCookie) {
      toast.error('User not authenticated. Please log in.')
      return
    }

    try {
      const user = JSON.parse(userCookie)
      let token = user.token

      const response = await axios.post(
        BASE_URL+`/students/${id}/passed-out`,
        {
          passed_out_date: passedOutDate,
        },
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        },
      )
      toast.success(response.data.message || 'Student marked as passed out successfully.')
      resetPassedOutModal()
      const fetchStudentData = async () => {
        try {
          const response = await ApiEndpoints.viewProfile(id)
          setData(response.data.data)
          setStudentData(response.data.data)
          setImg(response.data.data)
        } catch (error) {
          console.error('Error refreshing student data:', error)
        }
      }
      fetchStudentData()
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to mark student as passed out.')
      console.error(error.response?.data?.message)
    }
  }

  // Get course and class names
  const courseName = data?.course_name || 'N/A'
  const className = data?.class_name || 'N/A'

  const instituteName = data?.institute_id
    ? institutes?.find((insti) => insti.institute_id === data.institute_id)?.name || 'N/A'
    : 'N/A'

  const defaultStudentPhoto = `${process.env.PUBLIC_URL || ''}/studentprofile.jpg`
  const studentProfilePhotoSrc = (() => {
    const raw = data?.student_image ?? img?.student_image
    if (
      raw &&
      String(raw).trim() !== '' &&
      String(raw).toLowerCase() !== 'null'
    ) {
      return raw
    }
    return defaultStudentPhoto
  })()

  return (
    <>
      <style>
        {`
          .info-label {
            font-size: 12px;
            font-weight: 600;
            color: #6b7280;
            text-transform: uppercase;
            letter-spacing: 0.5px;
            margin-bottom: 6px;
            font-family: 'Inter', system-ui, sans-serif;
          }
          .info-value {
            font-size: 15px;
            font-weight: 500;
            color: #111827;
            font-family: 'Inter', system-ui, sans-serif;
          }
          .action-button {
            transition: all 0.2s ease;
          }
          .action-button:hover {
            transform: translateY(-2px);
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
          }
          .student-actions-row > [class*='col'] {
            display: flex;
          }
          .student-actions-row .action-button {
            height: 100%;
          }
          .student-actions-row .action-button span {
            min-height: 2.6em;
            display: flex;
            align-items: center;
            justify-content: center;
            text-align: center;
            line-height: 1.3;
          }
       .blink-updateButton {
          animation: blink 0.5s linear infinite;
          border: 2px solid #ff9900 !important;
        }
          @keyframes blink {
            50% {
              opacity: 0.5;
            }
          }
          @keyframes spin {
            from {
              transform: rotate(0deg);
            }
            to {
              transform: rotate(360deg);
            }
          }
        `}
      </style>

      <div 
        style={{ 
          marginLeft,
          transition: 'margin-left 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
          willChange: 'margin-left',
          WebkitTransition: 'margin-left 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
          position: 'relative',
          zIndex: 1,
          marginTop: '0px',
          paddingLeft: isCollapsed ? '12px' : '12px',
          paddingRight: isCollapsed ? '12px' : '12px',
        }}
      >
        {data ? (
          <>
            {/* Header */}
            <div
              className="container-fluid p-0"
              style={{
                marginTop: window.innerWidth < 992 ? '55px' : '-12px',
                paddingLeft: '0px',
                paddingRight: '0px',
              }}
            >
              <div style={{ marginLeft: isMobile ? '0' : '-40px' }}>
                <div 
                  className="d-flex justify-content-between align-items-center"
                  style={{
                    background: '#F3F4F6',
                    paddingLeft: '28px',
                    paddingRight: '28px',
                    paddingTop: '12px',
                    paddingBottom: '12px',
                    overflow: 'visible',
                    borderBottom: '1px solid #D1D3D4',
                    width: '100%',
                  }}
                >
                  {/* Left: Title and Update Profile Icon Button */}
                  <div className="d-flex align-items-center gap-3">
                    {/* School Logo */}
                    <div style={{ 
                      width: '40px', 
                      height: '40px', 
                      borderRadius: '50%', 
                      overflow: 'hidden', 
                      background: '#223843',
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center', 
                      flexShrink: 0,
                    }}>
                      {!logoError ? (
                        <img 
                          src={logo} 
                          alt="School Logo" 
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                          onError={() => setLogoError(true)}
                        />
                      ) : (
                        <LuGraduationCap size={20} style={{ color: '#ffffff' }} />
                      )}
                    </div>
                    
                    <h3 
                      className="m-0 fw-bold" 
                      style={{ 
                        letterSpacing: '-0.02em',
                        overflow: 'visible',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        fontSize: 'clamp(18px, 2.5vw, 22px)',
                        color: '#223843',
                        lineHeight: '1.2',
                        fontFamily: 'Inter, system-ui, sans-serif',
                      }}
                    >
                      {data?.name
                        ? data.name.charAt(0).toUpperCase() +
                          data.name.slice(1).toLowerCase()
                        : 'Student'} Profile
                    </h3>
                </div>
                  
                  {/* Right: Profile Icon with Dropdown */}
                  <div className="d-flex align-items-center gap-3" style={{ justifyContent: 'flex-end' }}>
                    {!isMobile && <ProfileDropdown logout={logout} />}
                  </div>
              </div>
            </div>
            </div>

            <Container 
              className="mt-4"
              style={{
                paddingLeft: isMobile ? '16px' : (isCollapsed ? '32px' : '40px'),
                paddingRight: isMobile ? '16px' : '32px',
                paddingTop: '32px',
                paddingBottom: '32px',
                maxWidth: '100%',
              }}
            >
                <div className="d-flex justify-content-end">
                <button 
                        onClick={handleUpdateStudentProfile}
                        className={highlightUpdate ? 'blink-updateButton' : ''}
                        style={{
                          borderRadius: '10px',
                          padding: '6px 10px',
                          height: '44px',
                          backgroundColor: '#223843',
                          border: 'none',
                          color: '#ffffff',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '6px',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          flexShrink: 0,
                          whiteSpace: 'nowrap',
                          marginTop: '-2.5rem',
                          marginBottom: '1rem',
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = '#1f323a'
                          e.currentTarget.style.transform = 'scale(1.05)'
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = '#223843'
                          e.currentTarget.style.transform = 'scale(1)'
                        }}
                        title="Update Profile"
                      >
                        <BsPencilFill size={18} />
                        <span style={{ fontSize: '12px', fontWeight: '650', fontFamily: 'Inter, system-ui, sans-serif' }}>
                          Update Profile
                        </span>
                </button>
                </div>
              {/* Profile Summary Section - Two Column Layout */}
              <Row className="mb-4">
                {/* Profile Summary Card (Left) */}
                <Col xs={12} md={8}>
                  <Card 
                    style={{
                      border: '1px solid #D1D3D4',
                      borderRadius: '16px',
                      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                      height: '100%',
                    }}
                  >
                    <Card.Body style={{ padding: '40px', textAlign: 'center' }}>
                      {/* Profile Photo with Back Button */}
                      <div className="mb-4" style={{ position: 'relative', display: 'inline-block' }}>
                      <div
                        style={{
                          width: '140px',
                          height: '140px',
                          borderRadius: '50%',
                          border: '4px solid #223843',
                          overflow: 'visible',
                          background: '#ffffff',
                          display: 'inline-block',
                          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                          position: 'relative',
                        }}
                      >
                        <img
                          src={studentProfilePhotoSrc}
                          alt="Profile"
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            borderRadius: '50%',
                          }}
                        />
                        {/* Back to List Button */}
                        <button
                          onClick={handleBack}
                          style={{
                            position: 'absolute',
                            bottom: '-8px',
                            right: '-8px',
                            width: '40px',
                            height: '40px',
                            borderRadius: '50%',
                            backgroundColor: '#223843',
                            border: '3px solid #ffffff',
                            color: '#ffffff',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
                            zIndex: 10,
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#223843'
                            e.currentTarget.style.transform = 'scale(1.1)'
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = '#223843'
                            e.currentTarget.style.transform = 'scale(1)'
                          }}
                          title="Back to Student list"
                        >
                          <FaArrowLeft size={16} />
                        </button>
                      </div>
                      </div>

                      {/* Student Name */}
                      <h2 className='text-uppercase'
                        style={{
                          color: '#111827',
                          fontWeight: '700',
                          fontSize: 'clamp(24px, 3vw, 32px)',
                          marginBottom: '12px',
                          fontFamily: 'Inter, system-ui, sans-serif',
                        }}
                      >
                        {data?.name
                          ? data.name.charAt(0).toUpperCase() +
                            data.name.slice(1).toLowerCase()
                          : 'N/A'}
                      </h2>

                      {/* Email/Admission Number */}
                      <div className="mb-4">
                        <p 
                         style={{
                            color: '#6b7280',
                            fontSize: '16px',
                            marginBottom: '8px',
                            fontFamily: 'Inter, system-ui, sans-serif',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '8px',
                          }}
                        >
                          <FaEnvelope size={16} />
                          {data?.email || data?.admission_number || 'N/A'}
                        </p>
                      </div>

                      {/* Badges */}
                      <div className="d-flex flex-wrap justify-content-center gap-2">
                        <span
                          style={{
                            backgroundColor: '#EFF6FF',
                            color: '#223843',
                            padding: '8px 16px',
                            borderRadius: '20px',
                            fontSize: '13px',
                            fontWeight: '600',
                            fontFamily: 'Inter, system-ui, sans-serif',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '6px',
                          }}
                        >
                          <BsPersonBadge />
                          {data?.admission_number || 'N/A'}
                        </span>
                        <span
                          style={{
                            backgroundColor: '#F0F9FF',
                            color: '#223843',
                            padding: '8px 16px',
                            borderRadius: '20px',
                            fontSize: '13px',
                            fontWeight: '600',
                            fontFamily: 'Inter, system-ui, sans-serif',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '6px',
                          }}
                        >
                          <FaIdCard />
                          Roll: {data?.roll_number || 'N/A'}
                        </span>
                        <span
                          style={{
                            backgroundColor: data?.status === 'active' ? '#ECFDF5' : '#FEE2E2',
                            color: data?.status === 'active' ? '#10B981' : '#EF4444',
                            padding: '8px 16px',
                            borderRadius: '20px',
                            fontSize: '13px',
                            fontWeight: '600',
                            fontFamily: 'Inter, system-ui, sans-serif',
                          }}
                        >
                          {data?.status
                            ? data.status.charAt(0).toUpperCase() +
                              data.status.slice(1)
                            : 'N/A'}
                        </span>
                        {courseName !== 'N/A' && (
                          <span
                            style={{
                              backgroundColor: '#EFF6FF',
                              color: '#223843',
                              padding: '8px 16px',
                              borderRadius: '20px',
                              fontSize: '13px',
                              fontWeight: '600',
                              fontFamily: 'Inter, system-ui, sans-serif',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '6px',
                            }}
                          >
                            <FaGraduationCap />
                            {courseName}
                          </span>
                        )}
                        {className !== 'N/A' && (
                          <span
                            style={{
                              backgroundColor: '#EFF6FF',
                              color: '#223843',
                              padding: '8px 16px',
                              borderRadius: '20px',
                              fontSize: '13px',
                              fontWeight: '600',
                              fontFamily: 'Inter, system-ui, sans-serif',
                            }}
                          >
                            {className}
                          </span>
                        )}
                        {data?.gender && (
                          <span
                            style={{
                              backgroundColor: '#F3F4F6',
                              color: '#111827',
                              padding: '8px 16px',
                              borderRadius: '20px',
                              fontSize: '13px',
                              fontWeight: '600',
                              fontFamily: 'Inter, system-ui, sans-serif',
                            }}
                          >
                            {data.gender}
                          </span>
                        )}
                      </div>

                      {/* Save as PDF Button */}
                      <div className="mt-4 d-flex justify-content-center">
                        <button
                          onClick={handleSaveAsPDF}
                          style={{
                            backgroundColor: '#10B981',
                            border: 'none',
                            color: '#ffffff',
                            borderRadius: '12px',
                            padding: '12px 24px',
                            fontWeight: '600',
                            fontSize: '14px',
                            fontFamily: 'Inter, system-ui, sans-serif',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '8px',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            boxShadow: '0 2px 4px rgba(16, 185, 129, 0.2)',
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#059669'
                            e.currentTarget.style.transform = 'translateY(-2px)'
                            e.currentTarget.style.boxShadow = '0 4px 8px rgba(16, 185, 129, 0.3)'
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = '#10B981'
                            e.currentTarget.style.transform = 'translateY(0)'
                            e.currentTarget.style.boxShadow = '0 2px 4px rgba(16, 185, 129, 0.2)'
                          }}
                        >
                          <BsFileEarmarkPdfFill size={18} /> Save as PDF
                        </button>
                      </div>
                    </Card.Body>
                  </Card>
                    </Col>

                {/* Side Highlight Card (Right) */}
                <Col xs={12} md={4}>
                  <Card 
                    style={{
                      border: '1px solid #D1D3D4',
                      borderRadius: '16px',
                      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                      background: 'linear-gradient(135deg, #F3F4F6 0%, #ffffff 100%)',
                      height: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginTop: isMobile ? '16px' : undefined,
                    }}
                  >
                    <Card.Body style={{ 
                      padding: '40px', 
                      textAlign: 'center',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      height: '100%',
                    }}>
                      <div style={{ width: '100%' }}>
                        <div
                          style={{
                            width: '80px',
                            height: '80px',
                            borderRadius: '50%',
                            backgroundColor: '#EFF6FF',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            marginBottom: '20px',
                          }}
                        >
                          <BsPrinterFill size={40} style={{ color: '#223843' }} />
                        </div>
                        <h5
                          style={{
                            color: '#111827',
                            fontWeight: '600',
                            fontSize: '18px',
                            marginBottom: '12px',
                            fontFamily: 'Inter, system-ui, sans-serif',
                          }}
                        >
                         ID Card
                        </h5>
                        <p
                          style={{
                            color: '#6b7280',
                            fontSize: '14px',
                            marginBottom: '24px',
                            fontFamily: 'Inter, system-ui, sans-serif',
                          }}
                        >
                          Generate and print student identification card
                        </p>
                        <Button
                          onClick={handleIdCard}
                          className="w-100 action-button"
                          style={{
                            backgroundColor: '#223843',
                            border: 'none',
                            color: '#ffffff',
                            borderRadius: '12px',
                            padding: '14px 28px',
                            fontWeight: '600',
                            fontSize: '16px',
                            fontFamily: 'Inter, system-ui, sans-serif',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '8px',
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#223843'
                            e.currentTarget.style.transform = 'translateY(-2px)'
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = '#223843'
                            e.currentTarget.style.transform = 'translateY(0)'
                          }}
                        >
                          <BsPrinterFill /> View ID Card
                        </Button>
                      </div>
                    </Card.Body>
                  </Card>
                    </Col>
                  </Row>

              {/* Personal Information Section */}
              <Row className="mb-4">
                <Col xs={12}>
                  <Card
                    style={{
                      border: '1px solid #D1D3D4',
                      borderRadius: '16px',
                      boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
                      overflow: 'hidden',
                    }}
                  >
                    {/* Header with Icon */}
                    <div
                      style={{
                        background: 'linear-gradient(135deg, #223843 0%, #223843 100%)',
                        padding: '16px 24px',
                        borderBottom: '1px solid #D1D3D4',
                      }}
                    >
                      <div className="d-flex align-items-center justify-content-between">
                        <div className="d-flex align-items-center gap-2">
                          <div
                            style={{
                              width: '36px',
                              height: '36px',
                              borderRadius: '10px',
                              backgroundColor: 'rgba(255, 255, 255, 0.2)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              backdropFilter: 'blur(10px)',
                            }}
                          >
                            <FaUser size={18} style={{ color: '#ffffff' }} />
                          </div>
                          <div>
                            <h5 className='text-start'
                              style={{
                                margin: 0,
                                fontWeight: '700',
                                color: '#ffffff',
                                fontSize: '18px',
                                fontFamily: 'Inter, system-ui, sans-serif',
                              }}
                            >
                              Personal Information
                            </h5>
                            <p
                              style={{
                                margin: '2px 0 0 0',
                                color: 'rgba(255, 255, 255, 0.9)',
                                fontSize: '12px',
                                fontFamily: 'Inter, system-ui, sans-serif',
                              }}
                            >
                              Student details and contact information
                            </p>
                          </div>
                        </div>
                        <button
                          onClick={() => setShowPersonalInfo(!showPersonalInfo)}
                          style={{
                            width: '36px',
                            height: '36px',
                            borderRadius: '50%',
                            backgroundColor: 'rgba(255, 255, 255, 0.2)',
                            border: 'none',
                            color: '#ffffff',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            flexShrink: 0,
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.3)'
                            e.currentTarget.style.transform = 'scale(1.1)'
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.2)'
                            e.currentTarget.style.transform = 'scale(1)'
                          }}
                          title={showPersonalInfo ? "Hide Section" : "Show Section"}
                        >
                          {showPersonalInfo ? <BsChevronUp size={18} /> : <BsChevronDown size={18} />}
                        </button>
                      </div>
                    </div>

                    {showPersonalInfo && (
                      <Card.Body style={{ padding: '20px' }}>
                        <Row className="g-3 text-start">
                          {/* Left Column */}
                        <Col xs={12} md={6}>
                          {/* Admission Details Card */}
                          <div
                            style={{
                              backgroundColor: '#F9FAFB',
                              borderRadius: '12px',
                              padding: '14px',
                              border: '1px solid #E5E7EB',
                              marginBottom: '12px',
                            }}
                          >
                            <h6
                              style={{
                                marginBottom: '10px',
                                fontWeight: '600',
                                color: '#223843',
                                fontSize: '12px',
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px',
                                fontFamily: 'Inter, system-ui, sans-serif',
                              }}
                            >
                              Admission Details
                            </h6>
                            <div className="d-flex flex-column gap-2">
                              <div
                                style={{
                                  backgroundColor: '#ffffff',
                                  borderRadius: '8px',
                                  padding: '8px 12px',
                                  border: '1px solid #E5E7EB',
                                }}
                              >
                                <div className="d-flex align-items-center gap-2 mb-1">
                                  <BsPersonBadge size={14} style={{ color: '#223843' }} />
                                  <div className="info-label" style={{ marginBottom: 0, fontSize: '11px' }}>Admission Number</div>
                                </div>
                                <div className="info-value" style={{ fontSize: '14px', fontWeight: '600', color: '#111827' }}>
                          {data?.admission_number || 'N/A'}
                      </div>
                      </div>

                              <div
                                style={{
                                  backgroundColor: '#ffffff',
                                  borderRadius: '8px',
                                  padding: '8px 12px',
                                  border: '1px solid #E5E7EB',
                                }}
                              >
                                <div className="d-flex align-items-center gap-2 mb-1">
                                  <FaIdCard size={14} style={{ color: '#223843' }} />
                                  <div className="info-label" style={{ marginBottom: 0, fontSize: '11px' }}>Roll Number</div>
                                </div>
                                <div className="info-value" style={{ fontSize: '14px', fontWeight: '600', color: '#111827' }}>
                                  {data?.roll_number || 'N/A'}
                                </div>
                              </div>

                              <div
                                style={{
                                  backgroundColor: '#ffffff',
                                  borderRadius: '8px',
                                  padding: '8px 12px',
                                  border: '1px solid #E5E7EB',
                                }}
                              >
                                <div className="d-flex align-items-center gap-2 mb-1">
                                  <BsCalendar3 size={14} style={{ color: '#223843' }} />
                                  <div className="info-label" style={{ marginBottom: 0, fontSize: '11px' }}>Admission Date</div>
                      </div>
                                <div className="info-value" style={{ fontSize: '14px', fontWeight: '600', color: '#111827' }}>
                                  {data?.admission_date || 'N/A'}
                      </div>
                      </div>

                              <div
                                style={{
                                  backgroundColor: '#ffffff',
                                  borderRadius: '8px',
                                  padding: '8px 12px',
                                  border: '1px solid #E5E7EB',
                                }}
                              >
                                <div className="d-flex align-items-center gap-2 mb-1">
                                  <BsBuilding size={14} style={{ color: '#223843' }} />
                                  <div className="info-label" style={{ marginBottom: 0, fontSize: '11px' }}>Institute Name</div>
                      </div>
                                <div className="info-value" style={{ fontSize: '14px', fontWeight: '600', color: '#111827' }}>
                                  {instituteName}
                      </div>
                      </div>
                            </div>
                          </div>

                          {/* Personal Details Card */}
                          <div
                            style={{
                              backgroundColor: '#F9FAFB',
                              borderRadius: '12px',
                              padding: '14px',
                              border: '1px solid #E5E7EB',
                            }}
                          >
                            <h6
                              style={{
                                marginBottom: '10px',
                                fontWeight: '600',
                                color: '#10B981',
                                fontSize: '12px',
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px',
                                fontFamily: 'Inter, system-ui, sans-serif',
                              }}
                            >
                              Personal Details
                            </h6>
                            <div className="d-flex flex-column gap-2">
                              <div
                                style={{
                                  backgroundColor: '#ffffff',
                                  borderRadius: '8px',
                                  padding: '8px 12px',
                                  border: '1px solid #E5E7EB',
                                }}
                              >
                                <div className="d-flex align-items-center gap-2 mb-1">
                                  <BsCalendar3 size={14} style={{ color: '#10B981' }} />
                                  <div className="info-label" style={{ marginBottom: 0, fontSize: '11px' }}>Birth Date</div>
                      </div>
                                <div className="info-value" style={{ fontSize: '14px', fontWeight: '600', color: '#111827' }}>
                                  {data?.dob || 'N/A'}
                      </div>
                              </div>

                              <div
                                style={{
                                  backgroundColor: '#ffffff',
                                  borderRadius: '8px',
                                  padding: '8px 12px',
                                  border: '1px solid #E5E7EB',
                                }}
                              >
                                <div className="d-flex align-items-center gap-2 mb-1">
                                  <FaUser size={14} style={{ color: '#10B981' }} />
                                  <div className="info-label" style={{ marginBottom: 0, fontSize: '11px' }}>Age</div>
                      </div>
                                <div className="info-value" style={{ fontSize: '14px', fontWeight: '600', color: '#111827' }}>
                                  {calculateAge(data?.dob) || 'N/A'} years
                                </div>
                              </div>

                              <div
                                style={{
                                  backgroundColor: '#ffffff',
                                  borderRadius: '8px',
                                  padding: '8px 12px',
                                  border: '1px solid #E5E7EB',
                                }}
                              >
                                <div className="d-flex align-items-center gap-2 mb-1">
                                  <FaVenusMars size={14} style={{ color: '#10B981' }} />
                                  <div className="info-label" style={{ marginBottom: 0, fontSize: '11px' }}>Gender</div>
                                </div>
                                <div className="info-value" style={{ fontSize: '14px', fontWeight: '600', color: '#111827' }}>
                                  {data?.gender || 'N/A'}
                                </div>
                              </div>
                            </div>
                      </div>
                    </Col>

                        {/* Right Column */}
                        <Col xs={12} md={6}>
                          {/* Contact Information Card */}
                          <div
                            style={{
                              backgroundColor: '#F9FAFB',
                              borderRadius: '12px',
                              padding: '14px',
                              border: '1px solid #E5E7EB',
                              marginBottom: '12px',
                            }}
                          >
                            <h6
                              style={{
                                marginBottom: '10px',
                                fontWeight: '600',
                                color: '#F59E0B',
                                fontSize: '12px',
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px',
                                fontFamily: 'Inter, system-ui, sans-serif',
                              }}
                            >
                              Contact Information
                            </h6>
                            <div className="d-flex flex-column gap-2">
                              <div
                                style={{
                                  backgroundColor: '#ffffff',
                                  borderRadius: '8px',
                                  padding: '8px 12px',
                                  border: '1px solid #E5E7EB',
                                }}
                              >
                                <div className="d-flex align-items-center gap-2 mb-1">
                                  <FaEnvelope size={14} style={{ color: '#F59E0B' }} />
                                  <div className="info-label" style={{ marginBottom: 0, fontSize: '11px' }}>Email Address</div>
                                </div>
                                <div className="info-value" style={{ fontSize: '14px', fontWeight: '600', color: '#111827' }}>
                                  {data?.email || 'N/A'}
                                </div>
                              </div>

                              <div
                                style={{
                                  backgroundColor: '#ffffff',
                                  borderRadius: '8px',
                                  padding: '8px 12px',
                                  border: '1px solid #E5E7EB',
                                }}
                              >
                                <div className="d-flex align-items-center gap-2 mb-1">
                                  <FaPhone size={14} style={{ color: '#F59E0B' }} />
                                  <div className="info-label" style={{ marginBottom: 0, fontSize: '11px' }}>Phone Number</div>
                                </div>
                                <div className="info-value" style={{ fontSize: '14px', fontWeight: '600', color: '#111827' }}>
                                  {data?.phone || 'N/A'}
                                </div>
                              </div>

                              <div
                                style={{
                                  backgroundColor: '#ffffff',
                                  borderRadius: '8px',
                                  padding: '8px 12px',
                                  border: '1px solid #E5E7EB',
                                }}
                              >
                                <div className="d-flex align-items-center gap-2 mb-1">
                                  <FaPhone size={14} style={{ color: '#F59E0B' }} />
                                  <div className="info-label" style={{ marginBottom: 0, fontSize: '11px' }}>Parent's Number</div>
                                </div>
                                <div className="info-value" style={{ fontSize: '14px', fontWeight: '600', color: '#111827' }}>
                                  {data?.parents_number || 'N/A'}
                                </div>
                              </div>

                              <div
                                style={{
                                  backgroundColor: '#ffffff',
                                  borderRadius: '8px',
                                  padding: '8px 12px',
                                  border: '1px solid #E5E7EB',
                                }}
                              >
                                <div className="d-flex align-items-center gap-2 mb-1">
                                  <FaWhatsapp size={14} style={{ color: '#F59E0B' }} />
                                  <div className="info-label" style={{ marginBottom: 0, fontSize: '11px' }}>WhatsApp Number</div>
                                </div>
                                <div className="info-value" style={{ fontSize: '14px', fontWeight: '600', color: '#111827' }}>
                                  {data?.whatsapp_number || 'N/A'}
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Additional Information Card */}
                          <div
                            style={{
                              backgroundColor: '#F9FAFB',
                              borderRadius: '12px',
                              padding: '14px',
                              border: '1px solid #E5E7EB',
                            }}
                          >
                            <h6
                              style={{
                                marginBottom: '10px',
                                fontWeight: '600',
                                color: '#223843',
                                fontSize: '12px',
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px',
                                fontFamily: 'Inter, system-ui, sans-serif',
                              }}
                            >
                              Additional Information
                            </h6>
                            <div className="d-flex flex-column gap-2">
                              <div
                                style={{
                                  backgroundColor: '#ffffff',
                                  borderRadius: '8px',
                                  padding: '8px 12px',
                                  border: '1px solid #E5E7EB',
                                }}
                              >
                                <div className="d-flex align-items-center gap-2 mb-1">
                                  <FaMapMarkerAlt size={14} style={{ color: '#10B981' }} />
                                  <div className="info-label" style={{ marginBottom: 0, fontSize: '11px' }}>Address</div>
                                </div>
                                <div className="info-value" style={{ fontSize: '14px', fontWeight: '600', color: '#111827' }}>
                                  {data?.address || 'N/A'}
                                </div>
                              </div>

                              <div
                                style={{
                                  backgroundColor: '#ffffff',
                                  borderRadius: '8px',
                                  padding: '8px 12px',
                                  border: '1px solid #E5E7EB',
                                }}
                              >
                                <div className="d-flex align-items-center gap-2 mb-1">
                                  <BsFileEarmarkText size={14} style={{ color: '#223843' }} />
                                  <div className="info-label" style={{ marginBottom: 0, fontSize: '11px' }}>ID Proof Number</div>
                                </div>
                                <div className="info-value" style={{ fontSize: '14px', fontWeight: '600', color: '#111827' }}>
                                  {data?.id_proof_number || 'N/A'}
                                </div>
                              </div>
                            </div>
                      </div>
                    </Col>
                      </Row>
                    </Card.Body>
                    )}
                  </Card>
                </Col>
              </Row>

              {/* Parent Information Section */}
              <Row className="mb-4">
                <Col xs={12}>
                  <Card
                    style={{
                      border: '1px solid #D1D3D4',
                      borderRadius: '16px',
                      boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
                      overflow: 'hidden',
                    }}
                  >
                    {/* Header with Icon */}
                    <div
                      style={{
                        background: 'linear-gradient(135deg, #F97316 0%, #EA580C 100%)',
                        padding: '16px 24px',
                        borderBottom: '1px solid #D1D3D4',
                      }}
                    >
                      <div className="d-flex align-items-center justify-content-between">
                        <div className="d-flex align-items-center gap-2">
                          <div
                            style={{
                              width: '36px',
                              height: '36px',
                              borderRadius: '10px',
                              backgroundColor: 'rgba(255, 255, 255, 0.2)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              backdropFilter: 'blur(10px)',
                            }}
                          >
                            <FaUsers size={18} style={{ color: '#ffffff' }} />
                          </div>
                          <div>
                            <h5
                              style={{
                                margin: 0,
                                fontWeight: '700',
                                color: '#ffffff',
                                fontSize: '18px',
                                fontFamily: 'Inter, system-ui, sans-serif',
                              }}
                            >
                              Parent Information
                            </h5>
                            <p className='text-start'
                              style={{
                                margin: '2px 0 0 0',
                                color: 'rgba(255, 255, 255, 0.9)',
                                fontSize: '12px',
                                fontFamily: 'Inter, system-ui, sans-serif',
                              }}
                            >
                              Guardian and parent details
                            </p>
                          </div>
                        </div>
                        <button
                          onClick={() => setShowParentInfo(!showParentInfo)}
                          style={{
                            width: '36px',
                            height: '36px',
                            borderRadius: '50%',
                            backgroundColor: 'rgba(255, 255, 255, 0.2)',
                            border: 'none',
                            color: '#ffffff',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            flexShrink: 0,
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.3)'
                            e.currentTarget.style.transform = 'scale(1.1)'
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.2)'
                            e.currentTarget.style.transform = 'scale(1)'
                          }}
                          title={showParentInfo ? "Hide Section" : "Show Section"}
                        >
                          {showParentInfo ? <BsChevronUp size={18} /> : <BsChevronDown size={18} />}
                        </button>
                      </div>
                    </div>

                    {showParentInfo && (
                      <Card.Body style={{ padding: '20px' }}>
                      <Row className="g-3 text-start">
                        <Col xs={12} md={6}>
                          <div
                            style={{
                              backgroundColor: '#F9FAFB',
                              borderRadius: '12px',
                              padding: '14px',
                              border: '1px solid #E5E7EB',
                              height: '100%',
                            }}
                          >
                            <div
                              style={{
                                backgroundColor: '#ffffff',
                                borderRadius: '8px',
                                padding: '12px',
                                border: '1px solid #E5E7EB',
                              }}
                            >
                              <div className="d-flex align-items-center gap-2 mb-1">
                                <div
                                  style={{
                                    width: '36px',
                                    height: '36px',
                                    borderRadius: '8px',
                                    backgroundColor: '#F3E8FF',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                  }}
                                >
                                  <FaUsers size={16} style={{ color: '#9333EA' }} />
                                </div>
                                <div style={{ flex: 1 }}>
                                  <div className="info-label" style={{ marginBottom: '2px', fontSize: '11px' }}>Father's Name</div>
                                  <div className="info-value" style={{ fontSize: '15px', fontWeight: '700', color: '#111827' }}>
                                    {data?.father_name || 'N/A'}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </Col>
                        <Col xs={12} md={6}>
                          <div
                            style={{
                              backgroundColor: '#F9FAFB',
                              borderRadius: '12px',
                              padding: '14px',
                              border: '1px solid #E5E7EB',
                              height: '100%',
                            }}
                          >
                            <div
                              style={{
                                backgroundColor: '#ffffff',
                                borderRadius: '8px',
                                padding: '12px',
                                border: '1px solid #E5E7EB',
                              }}
                            >
                              <div className="d-flex align-items-center gap-2 mb-1">
                                <div
                                  style={{
                                    width: '36px',
                                    height: '36px',
                                    borderRadius: '8px',
                                    backgroundColor: '#F3E8FF',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                  }}
                                >
                                  <FaUsers size={16} style={{ color: '#9333EA' }} />
                                </div>
                                <div style={{ flex: 1 }}>
                                  <div className="info-label" style={{ marginBottom: '2px', fontSize: '11px' }}>Mother's Name</div>
                                  <div className="info-value" style={{ fontSize: '15px', fontWeight: '700', color: '#111827' }}>
                                    {data?.mother_name || 'N/A'}
                                  </div>
                                </div>
                              </div>
                            </div>
                      </div>
                        </Col>
                      </Row>
                    </Card.Body>
                    )}
                  </Card>
                    </Col>
                  </Row>


              {/* Assign Guardian Section */}
              <Row className="mb-4">
                <Col xs={12}>
                  <Card
                    style={{
                      border: '1px solid #D1D3D4',
                      borderRadius: '16px',
                      boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
                      overflow: 'hidden',
                    }}
                  >
                    {/* Header with Icon */}
                    <div
                      style={{
                        background: 'linear-gradient(135deg, #223843 0%, #223843 100%)',
                        padding: '16px 24px',
                        borderBottom: '1px solid #D1D3D4',
                      }}
                    >
                      <div className="d-flex align-items-center justify-content-between">
                        <div className="d-flex align-items-center gap-2">
                          <div
                            style={{
                              width: '36px',
                              height: '36px',
                              borderRadius: '10px',
                              backgroundColor: 'rgba(255, 255, 255, 0.2)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              backdropFilter: 'blur(10px)',
                            }}
                          >
                            <FaShieldAlt size={18} style={{ color: '#ffffff' }} />
                          </div>
                          <div>
                            <h5 className='text-start'
                              style={{
                                margin: 0,
                                fontWeight: '700',
                                color: '#ffffff',
                                fontSize: '18px',
                                fontFamily: 'Inter, system-ui, sans-serif',
                              }}
                            >
                              Assign Guardian
                            </h5>
                            <p
                              style={{
                                margin: '2px 0 0 0',
                                color: 'rgba(255, 255, 255, 0.9)',
                                fontSize: '12px',
                                fontFamily: 'Inter, system-ui, sans-serif',
                              }}
                            >
                              Assign and manage guardian for this student
                            </p>
                          </div>
                        </div>
                        <button
                          onClick={() => setShowAssignGuardian(!showAssignGuardian)}
                          style={{
                            width: '36px',
                            height: '36px',
                            borderRadius: '50%',
                            backgroundColor: 'rgba(255, 255, 255, 0.2)',
                            border: 'none',
                            color: '#ffffff',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            flexShrink: 0,
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.3)'
                            e.currentTarget.style.transform = 'scale(1.1)'
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.2)'
                            e.currentTarget.style.transform = 'scale(1)'
                          }}
                          title={showAssignGuardian ? "Hide Section" : "Show Section"}
                        >
                          {showAssignGuardian ? <BsChevronUp size={18} /> : <BsChevronDown size={18} />}
                        </button>
                      </div>
                    </div>

                    {showAssignGuardian && (
                      <Card.Body style={{ padding: '20px' }}>
                      <Row className="g-3 align-items-end">
                        <Col xs={12} md={5} className="text-start">
                        <FormGroup>
                            <FormLabel 
                              style={{ 
                                fontSize: '12px', 
                                fontWeight: '600', 
                                color: '#6b7280', 
                                fontFamily: 'Inter, system-ui, sans-serif',
                                marginBottom: '8px',
                              }}
                            >
                              Guardian Email
                            </FormLabel>
                          <Form.Control
                            type="email"
                            placeholder="Enter Guardian Email"
                            value={parentEmail}
                            onChange={(e) => setParentEmail(e.target.value)} 
                              style={{
                                borderRadius: '8px',
                                border: '1px solid #D1D3D4',
                                padding: '10px 14px',
                                fontSize: '14px',
                                fontFamily: 'Inter, system-ui, sans-serif',
                                transition: 'all 0.2s ease',
                              }}
                              onFocus={(e) => {
                                e.target.style.borderColor = '#223843'
                                e.target.style.boxShadow = '0 0 0 3px rgba(37, 99, 235, 0.1)'
                              }}
                              onBlur={(e) => {
                                e.target.style.borderColor = '#D1D3D4'
                                e.target.style.boxShadow = 'none'
                              }}
                          />
                        </FormGroup>
                    </Col>
                        <Col xs={6} md={3}>
                      <div
                            className="d-flex align-items-center gap-2"
                            style={{ transition: 'all 0.2s ease', cursor: 'pointer' }}
                            onMouseEnter={(e) => {
                              const button = e.currentTarget.querySelector('button')
                              const text = e.currentTarget.querySelector('span')
                              if (button) {
                                button.style.backgroundColor = '#223843'
                                button.style.borderColor = '#223843'
                                button.style.transform = 'scale(1.05)'
                              }
                              if (text) {
                                text.style.transform = 'scale(1.05)'
                              }
                              e.currentTarget.style.transform = 'scale(1.02)'
                            }}
                            onMouseLeave={(e) => {
                              const button = e.currentTarget.querySelector('button')
                              const text = e.currentTarget.querySelector('span')
                              if (button) {
                                button.style.backgroundColor = '#223843'
                                button.style.borderColor = '#223843'
                                button.style.transform = 'scale(1)'
                              }
                              if (text) {
                                text.style.transform = 'scale(1)'
                              }
                              e.currentTarget.style.transform = 'scale(1)'
                            }}
                          >
                            <button
                              onClick={() => handleAssignParent(data.id)}
                              style={{
                                width: '48px',
                                height: '48px',
                                borderRadius: '50%',
                                border: '1px solid #223843',
                                backgroundColor: '#223843',
                                color: '#ffffff',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: 'pointer',
                                transition: 'all 0.2s ease',
                                flexShrink: 0,
                              }}
                              title="Assign Guardian"
                            >
                              <FaShieldAlt size={20} />
                            </button>
                              <span style={{ 
                                fontSize: '14px', 
                                color: '#111827', 
                                fontFamily: 'Inter, system-ui, sans-serif', 
                                fontWeight: '500',
                                transition: 'transform 0.2s ease',
                                display: 'inline-block',
                              }}>
                                Assign Guardian
                              </span>
                          </div>
                    </Col>
                        <Col xs={6} md={4}>
                      <div
                            className="d-flex align-items-center gap-2"
                            style={{ transition: 'all 0.2s ease', cursor: 'pointer' }}
                            onMouseEnter={(e) => {
                              const button = e.currentTarget.querySelector('button')
                              const text = e.currentTarget.querySelector('span')
                              if (button) {
                                button.style.backgroundColor = '#223843'
                                button.style.borderColor = '#223843'
                                button.style.color = '#ffffff'
                                button.style.transform = 'scale(1.05)'
                              }
                              if (text) {
                                text.style.transform = 'scale(1.05)'
                              }
                              e.currentTarget.style.transform = 'scale(1.02)'
                            }}
                            onMouseLeave={(e) => {
                              const button = e.currentTarget.querySelector('button')
                              const text = e.currentTarget.querySelector('span')
                              if (button) {
                                button.style.backgroundColor = '#ffffff'
                                button.style.borderColor = '#D1D3D4'
                                button.style.color = '#111827'
                                button.style.transform = 'scale(1)'
                              }
                              if (text) {
                                text.style.transform = 'scale(1)'
                              }
                              e.currentTarget.style.transform = 'scale(1)'
                            }}
                          >
                            <button
                              // onClick={() => navigate(`/AddGuardian`)}
                              onClick={handleOpenAllGuardiansInNewTab}
                              style={{
                                width: '48px',
                                height: '48px',
                                borderRadius: '50%',
                                border: '1px solid #D1D3D4',
                                backgroundColor: '#ffffff',
                                color: '#111827',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: 'pointer',
                                transition: 'all 0.2s ease',
                                flexShrink: 0,
                              }}
                              title="View All Guardians"
                            >
                              <FaEye size={20} />
                            </button>
                              <span className='d-flex text-start' style={{ 
                                fontSize: '14px', 
                                color: '#111827', 
                                fontFamily: 'Inter, system-ui, sans-serif', 
                                fontWeight: '500',
                                transition: 'transform 0.2s ease',
                                display: 'inline-block',
                              }}>
                                View All Guardians
                              </span>
                          </div>
                    </Col>
                  </Row>

                      {/* Assigned Guardians List */}
                      <div style={{ marginTop: '24px', paddingTop: '24px', borderTop: '1px solid #E5E7EB' }}>
                        <div className="d-flex align-items-center justify-content-between mb-3">
                          <div className="d-flex align-items-center gap-2">
                            <div
                              style={{
                                width: '32px',
                                height: '32px',
                                borderRadius: '8px',
                                backgroundColor: '#DBEAFE',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                              }}
                            >
                              <FaUsers size={16} style={{ color: '#223843' }} />
                            </div>
                            <h6
                              style={{
                                margin: 0,
                                fontWeight: '600',
                                color: '#111827',
                                fontSize: '14px',
                                fontFamily: 'Inter, system-ui, sans-serif',
                              }}
                            >
                              Assigned Guardians ({guardians.length})
                            </h6>
                          </div>
                          <button
                            onClick={fetchGuardians}
                            disabled={loadingGuardians}
                            style={{
                              width: '48px',
                              height: '48px',
                              borderRadius: '50%',
                              backgroundColor: '#223843',
                              border: 'none',
                              color: '#ffffff',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              cursor: loadingGuardians ? 'not-allowed' : 'pointer',
                              transition: 'all 0.2s ease',
                              opacity: loadingGuardians ? 0.7 : 1,
                            }}
                            onMouseEnter={(e) => {
                              if (!loadingGuardians) {
                                e.currentTarget.style.backgroundColor = '#223843'
                                e.currentTarget.style.transform = 'scale(1.05)'
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (!loadingGuardians) {
                                e.currentTarget.style.backgroundColor = '#223843'
                                e.currentTarget.style.transform = 'scale(1)'
                              }
                            }}
                            title="Refresh Guardians List"
                          >
                            <BsArrowClockwise 
                              size={20} 
                              style={{ 
                                animation: loadingGuardians ? 'spin 1s linear infinite' : 'none',
                              }} 
                            />
                          </button>
                        </div>

                        {loadingGuardians ? (
                          <div 
                            className="text-center py-4"
                            style={{
                              backgroundColor: '#F9FAFB',
                              borderRadius: '8px',
                              padding: '32px',
                              border: '1px solid #E5E7EB',
                            }}
                          >
                            <BsArrowClockwise 
                              size={24} 
                              style={{ 
                                color: '#223843',
                                animation: 'spin 1s linear infinite',
                                marginBottom: '12px',
                              }} 
                            />
                            <p style={{ color: '#6b7280', fontSize: '14px', fontFamily: 'Inter, system-ui, sans-serif', margin: 0 }}>
                              Loading guardians...
                            </p>
                          </div>
                        ) : guardians.length > 0 ? (
                          <Row className="g-3">
                            {guardians.map((guardian, index) => (
                              <Col xs={12} md={6} key={index}>
                                <div
                                  style={{
                                    backgroundColor: '#ffffff',
                                    borderRadius: '8px',
                                    padding: '12px 16px',
                                    border: '1px solid #E5E7EB',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '12px',
                                    transition: 'all 0.2s ease',
                                    height: '100%',
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor = '#F9FAFB'
                                    e.currentTarget.style.borderColor = '#223843'
                                    e.currentTarget.style.boxShadow = '0 2px 4px rgba(37, 99, 235, 0.1)'
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = '#ffffff'
                                    e.currentTarget.style.borderColor = '#E5E7EB'
                                    e.currentTarget.style.boxShadow = 'none'
                                  }}
                                >
                                  <div
                                    style={{
                                      width: '40px',
                                      height: '40px',
                                      borderRadius: '10px',
                                      backgroundColor: '#DBEAFE',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      flexShrink: 0,
                                    }}
                                  >
                                    <FaUsers size={18} style={{ color: '#223843' }} />
                                  </div>
                                  <div
                                    style={{
                                      flex: 1,
                                      display: 'flex',
                                      flexDirection: 'column',
                                      justifyContent: 'center',
                                      gap: '4px',
                                      minWidth: 0,
                                    }}
                                  >
                                    <div
                                      style={{
                                        fontSize: '14px',
                                        fontWeight: '600',
                                        color: '#111827',
                                        fontFamily: 'Inter, system-ui, sans-serif',
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                      }}
                                    >
                                      {guardian.name || guardian.parent_name || guardian.guardian_name || 'Guardian'}
                                    </div>
                                    {guardian.email && (
                                      <div
                                        className="d-flex align-items-start gap-2"
                                        style={{ minWidth: 0 }}
                                      >
                                        <FaEnvelope size={14} style={{ color: '#6b7280', flexShrink: 0 }} />
                                        <span style={{
                                          fontSize: '13px',
                                          color: '#6b7280',
                                          fontFamily: 'Inter, system-ui, sans-serif',
                                          minWidth: 0,
                                          overflowWrap: 'anywhere',
                                          wordBreak: 'break-word',
                                          lineHeight: '1.4',
                                        }}>
                                          {guardian.email}
                                        </span>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </Col>
                            ))}
                          </Row>
                        ) : (
                          <div
                            style={{
                              backgroundColor: '#F9FAFB',
                              borderRadius: '8px',
                              padding: '32px',
                              textAlign: 'center',
                              border: '1px dashed #D1D3D4',
                            }}
                          >
                            <div
                              style={{
                                width: '64px',
                                height: '64px',
                                borderRadius: '50%',
                                backgroundColor: '#E5E7EB',
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                marginBottom: '16px',
                              }}
                            >
                              <FaUsers size={28} style={{ color: '#9ca3af' }} />
                            </div>
                            <p
                              style={{
                                margin: 0,
                                color: '#111827',
                                fontSize: '15px',
                                fontWeight: '600',
                                fontFamily: 'Inter, system-ui, sans-serif',
                                marginBottom: '4px',
                              }}
                            >
                              No guardians assigned yet
                            </p>
                            <p
                              style={{
                                margin: 0,
                                color: '#6b7280',
                                fontSize: '13px',
                                fontFamily: 'Inter, system-ui, sans-serif',
                              }}
                            >
                              Assign a guardian using the form above
                            </p>
                          </div>
                        )}
                      </div>
                    </Card.Body>
                    )}
                  </Card>
                </Col>
              </Row>

              {/* Assign Transport Section */}
              <Row className="mb-4">
                <Col xs={12}>
                  <Card
                    style={{
                      border: '1px solid #D1D3D4',
                      borderRadius: '16px',
                      boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
                      overflow: 'hidden',
                    }}
                  >
                    <div
                      style={{
                        background: 'linear-gradient(135deg, #B45309 0%, #92400E 100%)',
                        padding: '16px 24px',
                        borderBottom: '1px solid #D1D3D4',
                      }}
                    >
                      <div className="d-flex align-items-center justify-content-between">
                        <div className="d-flex align-items-center gap-2">
                          <div
                            style={{
                              width: '36px',
                              height: '36px',
                              borderRadius: '10px',
                              backgroundColor: 'rgba(255, 255, 255, 0.2)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              backdropFilter: 'blur(10px)',
                            }}
                          >
                            <FaBus size={18} style={{ color: '#ffffff' }} />
                          </div>
                          <div>
                            <h5
                              className="text-start"
                              style={{
                                margin: 0,
                                fontWeight: '700',
                                color: '#ffffff',
                                fontSize: '18px',
                                fontFamily: 'Inter, system-ui, sans-serif',
                              }}
                            >
                              {isParentUser ? 'Assigned Transport' : 'Assign Transport'}
                            </h5>
                            <p
                              style={{
                                margin: '2px 0 0 0',
                                color: 'rgba(255, 255, 255, 0.9)',
                                fontSize: '12px',
                                fontFamily: 'Inter, system-ui, sans-serif',
                              }}
                            >
                              {isParentUser
                                ? 'Transport details for your child'
                                : 'Assign transport for your student'}
                            </p>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => setShowAssignTransport(!showAssignTransport)}
                          style={{
                            width: '36px',
                            height: '36px',
                            borderRadius: '50%',
                            backgroundColor: 'rgba(255, 255, 255, 0.2)',
                            border: 'none',
                            color: '#ffffff',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            flexShrink: 0,
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.3)'
                            e.currentTarget.style.transform = 'scale(1.1)'
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.2)'
                            e.currentTarget.style.transform = 'scale(1)'
                          }}
                          title={showAssignTransport ? 'Hide Section' : 'Show Section'}
                        >
                          {showAssignTransport ? <BsChevronUp size={18} /> : <BsChevronDown size={18} />}
                        </button>
                      </div>
                    </div>

                    {showAssignTransport && (
                      <Card.Body style={{ padding: '20px' }}>
                        {!isParentUser && (
                        <>
                        {(() => {
                          const normalizedSearch = transportForm.busSearch.trim().toLowerCase()
                          const filteredTransportOptions = normalizedSearch
                            ? transportOptions.filter((bus) => {
                                const haystack = `bus ${bus.bus_number || ''} ${bus.driver_name || ''} ${bus.bus_id || ''}`.toLowerCase()
                                return haystack.includes(normalizedSearch)
                              })
                            : transportOptions

                          return (
                        <Row className="g-3 align-items-start text-start">
                          <Col xs={12} md={6}>
                            <FormGroup className="mb-0">
                              <FormLabel
                                style={{
                                  fontSize: '12px',
                                  fontWeight: '600',
                                  color: '#6b7280',
                                  fontFamily: 'Inter, system-ui, sans-serif',
                                  marginBottom: '8px',
                                }}
                              >
                                Select Transport
                              </FormLabel>
                              <div style={{ position: 'relative' }}>
                              <Form.Control
                                type="text"
                                placeholder="Search vehicle number / driver name"
                                value={transportForm.busSearch}
                                onChange={(e) => {
                                  const nextSearch = e.target.value
                                  const selectedBus = transportOptions.find(
                                    (bus) =>
                                      nextSearch ===
                                      `Vehicle Number: ${bus.bus_number || '-'} - Driver: ${bus.driver_name || 'N/A'}`,
                                  )

                                  setTransportForm((f) => ({
                                    ...f,
                                    busSearch: nextSearch,
                                    busId: selectedBus ? String(selectedBus.bus_id) : '',
                                  }))
                                  setShowTransportDropdown(true)
                                }}
                                style={{
                                  borderRadius: '8px',
                                  border: '1px solid #D1D3D4',
                                  padding: '10px 14px',
                                  fontSize: '14px',
                                  fontFamily: 'Inter, system-ui, sans-serif',
                                  transition: 'all 0.2s ease',
                                }}
                                onFocus={(e) => {
                                  e.target.style.borderColor = '#B45309'
                                  e.target.style.boxShadow = '0 0 0 3px rgba(180, 83, 9, 0.12)'
                                  setShowTransportDropdown(true)
                                  if (transportOptions.length === 0) {
                                    fetchTransportOptions()
                                  }
                                }}
                                onBlur={(e) => {
                                  e.target.style.borderColor = '#D1D3D4'
                                  e.target.style.boxShadow = 'none'
                                  setTimeout(() => setShowTransportDropdown(false), 150)
                                }}
                              />
                              {showTransportDropdown && (
                                <div
                                  style={{
                                    position: 'absolute',
                                    top: '100%',
                                    left: 0,
                                    right: 0,
                                    marginTop: '6px',
                                    border: '1px solid #D1D3D4',
                                    borderRadius: '8px',
                                    maxHeight: '220px',
                                    overflowY: 'auto',
                                    backgroundColor: '#ffffff',
                                    zIndex: 20,
                                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                                  }}
                                >
                                  {filteredTransportOptions.length > 0 ? (
                                    filteredTransportOptions.map((bus) => (
                                      <button
                                        key={bus.bus_id}
                                        type="button"
                                        onMouseDown={() => {
                                          setTransportForm((f) => ({
                                            ...f,
                                            busId: String(bus.bus_id),
                                            busSearch: `Vehicle No. ${bus.bus_number || '-'} - Driver: ${bus.driver_name || 'N/A'}`,
                                          }))
                                          setShowTransportDropdown(false)
                                        }}
                                        style={{
                                          width: '100%',
                                          border: 'none',
                                          borderBottom: '1px solid #F3F4F6',
                                          background: '#ffffff',
                                          textAlign: 'left',
                                          padding: '10px 12px',
                                          fontSize: '13px',
                                          color: '#111827',
                                          fontFamily: 'Inter, system-ui, sans-serif',
                                          cursor: 'pointer',
                                        }}
                                      >
                                        {`Vehicle No. [ ${bus.bus_number || '-'}] - Driver: ${bus.driver_name || 'N/A'}`}
                                      </button>
                                    ))
                                  ) : (
                                    <div
                                      style={{
                                        padding: '10px 12px',
                                        color: '#6b7280',
                                        fontSize: '13px',
                                        fontFamily: 'Inter, system-ui, sans-serif',
                                      }}
                                    >
                                      No transport found
                                    </div>
                                  )}
                                </div>
                              )}
                              </div>
                            </FormGroup>
                          </Col>
                          <Col xs={12} md={6}>
                            <FormGroup className="mb-0">
                              <FormLabel
                                style={{
                                  fontSize: '12px',
                                  fontWeight: '600',
                                  color: '#6b7280',
                                  fontFamily: 'Inter, system-ui, sans-serif',
                                  marginBottom: '8px',
                                }}
                              >
                                Enter Pick up
                              </FormLabel>
                              <Form.Control
                                type="text"
                                placeholder="Enter pick up address (optional)"
                                value={transportForm.pickup}
                                onChange={(e) => {
                                  setTransportForm((f) => ({
                                    ...f,
                                    pickup: e.target.value,
                                  }))
                                }}
                                style={{
                                  borderRadius: '8px',
                                  border: '1px solid #D1D3D4',
                                  padding: '10px 14px',
                                  fontSize: '14px',
                                  fontFamily: 'Inter, system-ui, sans-serif',
                                  transition: 'all 0.2s ease',
                                }}
                                onFocus={(e) => {
                                  e.target.style.borderColor = '#B45309'
                                  e.target.style.boxShadow = '0 0 0 3px rgba(180, 83, 9, 0.12)'
                                }}
                                onBlur={(e) => {
                                  e.target.style.borderColor = '#D1D3D4'
                                  e.target.style.boxShadow = 'none'
                                }}
                              />
                            </FormGroup>
                          </Col>
                        </Row>
                          )
                        })()}

                        <Row className="g-3 mt-3 align-items-center flex-column flex-md-row justify-content-start justify-content-md-end">
                          <Col xs={12} md="auto" className="d-flex justify-content-start justify-content-md-end">
                            <div
                              className="d-flex align-items-center gap-2 flex-shrink-0"
                              style={{ transition: 'all 0.2s ease', cursor: 'pointer' }}
                              onMouseEnter={(e) => {
                                const button = e.currentTarget.querySelector('button')
                                const text = e.currentTarget.querySelector('span')
                                if (button) {
                                  button.style.backgroundColor = '#B45309'
                                  button.style.borderColor = '#B45309'
                                  button.style.transform = 'scale(1.05)'
                                }
                                if (text) text.style.transform = 'scale(1.05)'
                                e.currentTarget.style.transform = 'scale(1.02)'
                              }}
                              onMouseLeave={(e) => {
                                const button = e.currentTarget.querySelector('button')
                                const text = e.currentTarget.querySelector('span')
                                if (button) {
                                  button.style.backgroundColor = '#B45309'
                                  button.style.borderColor = '#B45309'
                                  button.style.transform = 'scale(1)'
                                }
                                if (text) text.style.transform = 'scale(1)'
                                e.currentTarget.style.transform = 'scale(1)'
                              }}
                            >
                              <button
                                type="button"
                                onClick={handleAssignTransport}
                                disabled={assigningTransport}
                                style={{
                                  width: '48px',
                                  height: '48px',
                                  borderRadius: '50%',
                                  border: '1px solid #B45309',
                                  backgroundColor: '#B45309',
                                  color: '#ffffff',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  cursor: assigningTransport ? 'not-allowed' : 'pointer',
                                  transition: 'all 0.2s ease',
                                  flexShrink: 0,
                                  opacity: assigningTransport ? 0.7 : 1,
                                }}
                                title="Assign Transport"
                              >
                                {assigningTransport ? <BsArrowClockwise size={20} style={{ animation: 'spin 1s linear infinite' }} /> : <FaBus size={20} />}
                              </button>
                              <span
                                className="text-nowrap"
                                style={{
                                  fontSize: '14px',
                                  color: '#111827',
                                  fontFamily: 'Inter, system-ui, sans-serif',
                                  fontWeight: '500',
                                  transition: 'transform 0.2s ease',
                                  display: 'inline-block',
                                }}
                              >
                                {assigningTransport ? 'Assigning...' : 'Assign Transport'}
                              </span>
                            </div>
                          </Col>
                          <Col xs={12} md="auto" className="d-flex justify-content-start justify-content-md-end">
                            <div
                              className="d-flex align-items-center gap-2 flex-shrink-0"
                              style={{ transition: 'all 0.2s ease', cursor: 'pointer' }}
                              onMouseEnter={(e) => {
                                const button = e.currentTarget.querySelector('button')
                                const text = e.currentTarget.querySelector('span')
                                if (button) {
                                  button.style.backgroundColor = '#B45309'
                                  button.style.borderColor = '#B45309'
                                  button.style.color = '#ffffff'
                                  button.style.transform = 'scale(1.05)'
                                }
                                if (text) text.style.transform = 'scale(1.05)'
                                e.currentTarget.style.transform = 'scale(1.02)'
                              }}
                              onMouseLeave={(e) => {
                                const button = e.currentTarget.querySelector('button')
                                const text = e.currentTarget.querySelector('span')
                                if (button) {
                                  button.style.backgroundColor = '#ffffff'
                                  button.style.borderColor = '#D1D3D4'
                                  button.style.color = '#111827'
                                  button.style.transform = 'scale(1)'
                                }
                                if (text) text.style.transform = 'scale(1)'
                                e.currentTarget.style.transform = 'scale(1)'
                              }}
                            >
                              <button
                                type="button"
                                onClick={() => navigate(`/Transport`)}
                                style={{
                                  width: '48px',
                                  height: '48px',
                                  borderRadius: '50%',
                                  border: '1px solid #D1D3D4',
                                  backgroundColor: '#ffffff',
                                  color: '#111827',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  cursor: 'pointer',
                                  transition: 'all 0.2s ease',
                                  flexShrink: 0,
                                }}
                                title="View All Transport"
                              >
                                <FaEye size={20} />
                              </button>
                              <span
                                className="d-flex text-start text-nowrap"
                                style={{
                                  fontSize: '14px',
                                  color: '#111827',
                                  fontFamily: 'Inter, system-ui, sans-serif',
                                  fontWeight: '500',
                                  transition: 'transform 0.2s ease',
                                  display: 'inline-block',
                                }}
                              >
                                View All Transport
                              </span>
                            </div>
                          </Col>
                        </Row>
                        </>
                        )}

                        <div
                          style={{
                            marginTop: isParentUser ? '0px' : '24px',
                            paddingTop: isParentUser ? '0px' : '24px',
                            borderTop: isParentUser ? 'none' : '1px solid #E5E7EB',
                          }}
                        >
                          <div className="d-flex align-items-center justify-content-between mb-3">
                            <div className="d-flex align-items-center gap-2">
                              <div
                                style={{
                                  width: '32px',
                                  height: '32px',
                                  borderRadius: '8px',
                                  backgroundColor: '#FFEDD5',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                }}
                              >
                                <FaBus size={16} style={{ color: '#B45309' }} />
                              </div>
                              <h6
                                style={{
                                  margin: 0,
                                  fontWeight: '600',
                                  color: '#111827',
                                  fontSize: '14px',
                                  fontFamily: 'Inter, system-ui, sans-serif',
                                }}
                              >
                                Assigned transport ({assignedTransports.length})
                              </h6>
                            </div>
                            <button
                              type="button"
                              onClick={refreshTransportList}
                              disabled={loadingTransportRefresh}
                              style={{
                                width: '48px',
                                height: '48px',
                                borderRadius: '50%',
                                backgroundColor: '#B45309',
                                border: 'none',
                                color: '#ffffff',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: loadingTransportRefresh ? 'not-allowed' : 'pointer',
                                transition: 'all 0.2s ease',
                                opacity: loadingTransportRefresh ? 0.7 : 1,
                              }}
                              onMouseEnter={(e) => {
                                if (!loadingTransportRefresh) {
                                  e.currentTarget.style.transform = 'scale(1.05)'
                                }
                              }}
                              onMouseLeave={(e) => {
                                if (!loadingTransportRefresh) {
                                  e.currentTarget.style.transform = 'scale(1)'
                                }
                              }}
                              title="Refresh list"
                            >
                              <BsArrowClockwise
                                size={20}
                                style={{
                                  animation: loadingTransportRefresh ? 'spin 1s linear infinite' : 'none',
                                }}
                              />
                            </button>
                          </div>

                          {loadingTransportRefresh || loadingAssignedTransportSync ? (
                            <div
                              className="text-center py-4"
                              style={{
                                backgroundColor: '#F9FAFB',
                                borderRadius: '8px',
                                padding: '32px',
                                border: '1px solid #E5E7EB',
                              }}
                            >
                              <BsArrowClockwise
                                size={24}
                                style={{
                                  color: '#B45309',
                                  animation: 'spin 1s linear infinite',
                                  marginBottom: '12px',
                                }}
                              />
                              <p
                                style={{
                                  color: '#6b7280',
                                  fontSize: '14px',
                                  fontFamily: 'Inter, system-ui, sans-serif',
                                  margin: 0,
                                }}
                              >
                                {loadingTransportRefresh ? 'Refreshing...' : 'Loading assigned transport...'}
                              </p>
                            </div>
                          ) : assignedTransportError ? (
                            <div
                              className="text-center py-4"
                              style={{
                                backgroundColor: '#FEF2F2',
                                borderRadius: '8px',
                                padding: '32px',
                                border: '1px solid #FECACA',
                              }}
                            >
                              <FaExclamationCircle
                                size={28}
                                style={{ color: '#DC2626', marginBottom: '12px' }}
                              />
                              <p
                                style={{
                                  color: '#991B1B',
                                  fontSize: '14px',
                                  fontFamily: 'Inter, system-ui, sans-serif',
                                  margin: 0,
                                  fontWeight: 600,
                                }}
                              >
                                {assignedTransportError}
                              </p>
                            </div>
                          ) : assignedTransports.length > 0 ? (
                            <Row className="g-3 text-start">
                              {assignedTransports.map((t) => (
                                <Col xs={12} md={6} key={t.id}>
                                  <div
                                    style={{
                                      backgroundColor: '#ffffff',
                                      borderRadius: '8px',
                                      padding: '14px 16px',
                                      border: '1px solid #E5E7EB',
                                      transition: 'all 0.2s ease',
                                      height: '100%',
                                    }}
                                    onMouseEnter={(e) => {
                                      e.currentTarget.style.backgroundColor = '#FFFBEB'
                                      e.currentTarget.style.borderColor = '#B45309'
                                      e.currentTarget.style.boxShadow = '0 2px 4px rgba(180, 83, 9, 0.12)'
                                    }}
                                    onMouseLeave={(e) => {
                                      e.currentTarget.style.backgroundColor = '#ffffff'
                                      e.currentTarget.style.borderColor = '#E5E7EB'
                                      e.currentTarget.style.boxShadow = 'none'
                                    }}
                                  >
                                    <div className="d-flex align-items-start gap-3">
                                      <div
                                        style={{
                                          width: '40px',
                                          height: '40px',
                                          borderRadius: '10px',
                                          backgroundColor: '#FFEDD5',
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          flexShrink: 0,
                                        }}
                                      >
                                        <FaBus size={18} style={{ color: '#B45309' }} />
                                      </div>
                                      <div style={{ flex: 1, minWidth: 0 }}>
                                        <div
                                          className="d-flex align-items-center justify-content-between gap-2"
                                          style={{
                                            fontSize: '14px',
                                            fontWeight: '600',
                                            color: '#111827',
                                            fontFamily: 'Inter, system-ui, sans-serif',
                                            marginBottom: '6px',
                                            flexWrap: 'nowrap',
                                          }}
                                        >
                                          <span style={{ minWidth: 0 }}>
                                            Vehicle No.:{' '}
                                            {t.bus_number || (t.bus_id ? `#${t.bus_id}` : '-')}
                                          </span>
                                          {!isParentUser ? (
                                            <button
                                              type="button"
                                              onClick={handleUnassignTransport}
                                              disabled={removingTransport || assigningTransport}
                                              style={{
                                                borderRadius: '8px',
                                                border: '1px solid #DC2626',
                                                backgroundColor: '#ffffff',
                                                color: '#DC2626',
                                                fontSize: '12px',
                                                fontWeight: '600',
                                                fontFamily: 'Inter, system-ui, sans-serif',
                                                padding: '6px 12px',
                                                cursor:
                                                  removingTransport || assigningTransport
                                                    ? 'not-allowed'
                                                    : 'pointer',
                                                opacity: removingTransport || assigningTransport ? 0.7 : 1,
                                                whiteSpace: 'nowrap',
                                                flexShrink: 0,
                                              }}
                                              title="Unassign"
                                            >
                                              {removingTransport ? 'Removing...' : 'Unassign'}
                                            </button>
                                          ) : null}
                                        </div>
                                        <div
                                          className="d-flex flex-wrap align-items-center gap-2"
                                          style={{ fontSize: '13px', color: '#6b7280' }}
                                        >
                                          <span
                                            style={{
                                              fontWeight: '600',
                                              color: '#374151',
                                              fontFamily: 'Inter, system-ui, sans-serif',
                                            }}
                                          >
                                            Driver Contact Number:{' '}
                                            {t.driver_contact_number || '-'}
                                          </span>
                                          {t.driver_name ? (
                                            <>
                                              <span style={{ color: '#D1D5DB' }}>|</span>
                                              <span style={{ color: '#374151', fontWeight: '500' }}>
                                                Driver Name: {t.driver_name}
                                              </span>
                                            </>
                                          ) : null}
                                        </div>
                                        {t.pickup ? (
                                          <div
                                            style={{
                                              marginTop: '8px',
                                              fontSize: '13px',
                                              color: '#374151',
                                              fontFamily: 'Inter, system-ui, sans-serif',
                                              wordBreak: 'break-word',
                                            }}
                                          >
                                            <span style={{ fontWeight: '600' }}>Pick up: </span>
                                            {t.pickup}
                                          </div>
                                        ) : null}
                                      </div>
                                    </div>
                                  </div>
                                </Col>
                              ))}
                            </Row>
                          ) : (
                            <div
                              style={{
                                backgroundColor: '#F9FAFB',
                                borderRadius: '8px',
                                padding: '32px',
                                textAlign: 'center',
                                border: '1px dashed #D1D3D4',
                              }}
                            >
                              <div
                                style={{
                                  width: '64px',
                                  height: '64px',
                                  borderRadius: '50%',
                                  backgroundColor: '#E5E7EB',
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  marginBottom: '16px',
                                }}
                              >
                                <FaBus size={28} style={{ color: '#9ca3af' }} />
                              </div>
                              <p
                                style={{
                                  margin: 0,
                                  color: '#111827',
                                  fontSize: '15px',
                                  fontWeight: '600',
                                  fontFamily: 'Inter, system-ui, sans-serif',
                                  marginBottom: '4px',
                                }}
                              >
                                No transport assigned yet
                              </p>
                              <p
                                style={{
                                  margin: 0,
                                  color: '#6b7280',
                                  fontSize: '13px',
                                  fontFamily: 'Inter, system-ui, sans-serif',
                                }}
                              >
                                {isParentUser
                                  ? 'This student has no assigned transport yet.'
                                  : 'Search a transport and click assign'}
                              </p>
                            </div>
                          )}
                        </div>
                      </Card.Body>
                    )}
                  </Card>
                </Col>
              </Row>

              {/* Student Actions Section */}
              <Row>
                <Col xs={12}>
                  <Card
                    style={{
                      border: '1px solid #D1D3D4',
                      borderRadius: '16px',
                      boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
                      overflow: 'hidden',
                    }}
                  >
                    {/* Header with Icon */}
                    <div
                      style={{
                        background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
                        padding: '16px 24px',
                        borderBottom: '1px solid #D1D3D4',
                      }}
                    >
                      <div className="d-flex align-items-center justify-content-between">
                        <div className="d-flex align-items-center gap-2">
                          <div
                            style={{
                              width: '36px',
                              height: '36px',
                              borderRadius: '10px',
                              backgroundColor: 'rgba(255, 255, 255, 0.2)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              backdropFilter: 'blur(10px)',
                            }}
                          >
                            <FaGraduationCap size={18} style={{ color: '#ffffff' }} />
                          </div>
                          <div>
                            <h5 className='text-start'
                              style={{
                                margin: 0,
                                fontWeight: '700',
                                color: '#ffffff',
                                fontSize: '18px',
                                fontFamily: 'Inter, system-ui, sans-serif',
                              }}
                            >
                              Student Actions
                            </h5>
                            <p
                              style={{
                                margin: '2px 0 0 0',
                                color: 'rgba(255, 255, 255, 0.9)',
                                fontSize: '12px',
                                fontFamily: 'Inter, system-ui, sans-serif',
                              }}
                            >
                              Quick access to student management features
                            </p>
                          </div>
                        </div>
                        <button
                          onClick={() => setShowStudentActions(!showStudentActions)}
                          style={{
                            width: '36px',
                            height: '36px',
                            borderRadius: '50%',
                            backgroundColor: 'rgba(255, 255, 255, 0.2)',
                            border: 'none',
                            color: '#ffffff',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            flexShrink: 0,
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.3)'
                            e.currentTarget.style.transform = 'scale(1.1)'
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.2)'
                            e.currentTarget.style.transform = 'scale(1)'
                          }}
                          title={showStudentActions ? "Hide Section" : "Show Section"}
                        >
                          {showStudentActions ? <BsChevronUp size={18} /> : <BsChevronDown size={18} />}
                        </button>
                      </div>
                    </div>

                    {showStudentActions && (
                      <Card.Body style={{ padding: '20px' }}>
                      <Row className="g-3 student-actions-row">
                        <Col xs={6} sm={4} md={2.4}>
                      <Button
                            variant="light"
                            onClick={() => handleViewFees(data.id, data.course_id, data.class_id)}
                            className="w-100 action-button"
                            style={{
                              backgroundColor: '#EFF6FF',
                              border: '1px solid #DBEAFE',
                              color: '#223843',
                              borderRadius: '10px',
                              padding: '16px 12px',
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              gap: '8px',
                              fontFamily: 'Inter, system-ui, sans-serif',
                              fontWeight: '600',
                              transition: 'all 0.2s ease',
                              minHeight: '100px',
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = '#DBEAFE'
                              e.currentTarget.style.transform = 'translateY(-3px)'
                              e.currentTarget.style.boxShadow = '0 4px 12px rgba(37, 99, 235, 0.2)'
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = '#EFF6FF'
                              e.currentTarget.style.transform = 'translateY(0)'
                              e.currentTarget.style.boxShadow = 'none'
                            }}
                          >
                            <div
                              style={{
                                width: '44px',
                                height: '44px',
                                borderRadius: '12px',
                                backgroundColor: '#223843',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                marginBottom: '4px',
                              }}
                            >
                              <FaWallet size={20} style={{ color: '#ffffff' }} />
                            </div>
                            <span style={{ fontSize: '13px', fontWeight: '600' }}>Fees</span>
                      </Button>
                    </Col>
                        <Col xs={6} sm={4} md={2.4}>
                      <Button
                            variant="light"
                        onClick={handleViewCourse}
                            className="w-100 action-button"
                            style={{
                              backgroundColor: '#ECFDF5',
                              border: '1px solid #D1FAE5',
                              color: '#10B981',
                              borderRadius: '10px',
                              padding: '16px 12px',
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              gap: '8px',
                              fontFamily: 'Inter, system-ui, sans-serif',
                              fontWeight: '600',
                              transition: 'all 0.2s ease',
                              minHeight: '100px',
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = '#D1FAE5'
                              e.currentTarget.style.transform = 'translateY(-3px)'
                              e.currentTarget.style.boxShadow = '0 4px 12px rgba(16, 185, 129, 0.2)'
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = '#ECFDF5'
                              e.currentTarget.style.transform = 'translateY(0)'
                              e.currentTarget.style.boxShadow = 'none'
                            }}
                          >
                            <div
                              style={{
                                width: '44px',
                                height: '44px',
                                borderRadius: '12px',
                                backgroundColor: '#10B981',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                marginBottom: '4px',
                              }}
                            >
                              <FaGraduationCap size={20} style={{ color: '#ffffff' }} />
                            </div>
                            <span style={{ fontSize: '13px', fontWeight: '600' }}>Courses</span>
                      </Button>
                    </Col>
                        <Col xs={6} sm={4} md={2.4}>
                      <Button
                            variant="light"
                            onClick={() =>
                              handleViewAttendance(
                                data.id,
                                profileNavCourseId,
                                profileNavClassId,
                              )}
                            className="w-100 action-button"
                            style={{
                              backgroundColor: '#FEF3C7',
                              border: '1px solid #FDE68A',
                              color: '#F59E0B',
                              borderRadius: '10px',
                              padding: '16px 12px',
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              gap: '8px',
                              fontFamily: 'Inter, system-ui, sans-serif',
                              fontWeight: '600',
                              transition: 'all 0.2s ease',
                              minHeight: '100px',
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = '#FDE68A'
                              e.currentTarget.style.transform = 'translateY(-3px)'
                              e.currentTarget.style.boxShadow = '0 4px 12px rgba(245, 158, 11, 0.2)'
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = '#FEF3C7'
                              e.currentTarget.style.transform = 'translateY(0)'
                              e.currentTarget.style.boxShadow = 'none'
                            }}
                          >
                            <div
                              style={{
                                width: '44px',
                                height: '44px',
                                borderRadius: '12px',
                                backgroundColor: '#F59E0B',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                marginBottom: '4px',
                              }}
                            >
                              <FaCalendarCheck size={20} style={{ color: '#ffffff' }} />
                            </div>
                            <span style={{ fontSize: '13px', fontWeight: '600' }}>Attendance</span>
                      </Button>
                    </Col>
                        <Col xs={6} sm={4} md={2.4}>
                      <Button
                            variant="light"
                            onClick={handleViewCertificate}
                            className="w-100 action-button"
                            style={{
                              backgroundColor: '#F3E8FF',
                              border: '1px solid #E9D5FF',
                              color: '#9333EA',
                              borderRadius: '10px',
                              padding: '16px 12px',
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              gap: '8px',
                              fontFamily: 'Inter, system-ui, sans-serif',
                              fontWeight: '600',
                              transition: 'all 0.2s ease',
                              minHeight: '100px',
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = '#E9D5FF'
                              e.currentTarget.style.transform = 'translateY(-3px)'
                              e.currentTarget.style.boxShadow = '0 4px 12px rgba(147, 51, 234, 0.2)'
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = '#F3E8FF'
                              e.currentTarget.style.transform = 'translateY(0)'
                              e.currentTarget.style.boxShadow = 'none'
                            }}
                          >
                            <div
                              style={{
                                width: '44px',
                                height: '44px',
                                borderRadius: '12px',
                                backgroundColor: '#9333EA',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                marginBottom: '4px',
                              }}
                            >
                              <BsFileEarmarkText size={20} style={{ color: '#ffffff' }} />
                            </div>
                            <span style={{ fontSize: '13px', fontWeight: '600' }}>View Certificate</span>
                      </Button>
                    </Col>
                        <Col xs={6} sm={4} md={2.4}>
                      <Button
                            variant="light"
                            onClick={handleViewMarksheet}
                            className="w-100 action-button"
                            style={{
                              backgroundColor: '#F0FDF4',
                              border: '1px solid #BBF7D0',
                              color: '#16A34A',
                              borderRadius: '10px',
                              padding: '16px 12px',
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              gap: '8px',
                              fontFamily: 'Inter, system-ui, sans-serif',
                              fontWeight: '600',
                              transition: 'all 0.2s ease',
                              minHeight: '100px',
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = '#BBF7D0'
                              e.currentTarget.style.transform = 'translateY(-3px)'
                              e.currentTarget.style.boxShadow = '0 4px 12px rgba(22, 163, 74, 0.2)'
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = '#F0FDF4'
                              e.currentTarget.style.transform = 'translateY(0)'
                              e.currentTarget.style.boxShadow = 'none'
                            }}
                          >
                            <div
                              style={{
                                width: '44px',
                                height: '44px',
                                borderRadius: '12px',
                                backgroundColor: '#16A34A',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                marginBottom: '4px',
                              }}
                            >
                              <BsFileEarmarkPdfFill size={20} style={{ color: '#ffffff' }} />
                            </div>
                            <span style={{ fontSize: '13px', fontWeight: '600' }}>View Marksheet</span>
                      </Button>
                    </Col>
                        <Col xs={6} sm={4} md={2.4}>
                      <Button
                            variant="light"
                            onClick={handleViewTransferCertificate}
                            className="w-100 action-button"
                            style={{
                              backgroundColor: '#F0FDFA',
                              border: '1px solid #99F6E4',
                              color: '#0F766E',
                              borderRadius: '10px',
                              padding: '16px 12px',
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              gap: '8px',
                              fontFamily: 'Inter, system-ui, sans-serif',
                              fontWeight: '600',
                              transition: 'all 0.2s ease',
                              minHeight: '100px',
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = '#99F6E4'
                              e.currentTarget.style.transform = 'translateY(-3px)'
                              e.currentTarget.style.boxShadow = '0 4px 12px rgba(15, 118, 110, 0.2)'
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = '#F0FDFA'
                              e.currentTarget.style.transform = 'translateY(0)'
                              e.currentTarget.style.boxShadow = 'none'
                            }}
                          >
                            <div
                              style={{
                                width: '44px',
                                height: '44px',
                                borderRadius: '12px',
                                backgroundColor: '#0F766E',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                marginBottom: '4px',
                              }}
                            >
                              <BsFileEarmarkText size={20} style={{ color: '#ffffff' }} />
                            </div>
                            <span style={{ fontSize: '13px', fontWeight: '600', textAlign: 'center' }}>
                              View Leave/Transfer Certificate
                            </span>
                      </Button>
                    </Col>
                        <Col xs={6} sm={4} md={2.4}>
                      <Button
                            variant="light"
                            onClick={() => handleViewHomework(data.id)}
                            className="w-100 action-button"
                            style={{
                              backgroundColor: '#FFF7ED',
                              border: '1px solid #FFEDD5',
                              color: '#EA580C',
                              borderRadius: '10px',
                              padding: '16px 12px',
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              gap: '8px',
                              fontFamily: 'Inter, system-ui, sans-serif',
                              fontWeight: '600',
                              transition: 'all 0.2s ease',
                              minHeight: '100px',
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = '#FFEDD5'
                              e.currentTarget.style.transform = 'translateY(-3px)'
                              e.currentTarget.style.boxShadow = '0 4px 12px rgba(234, 88, 12, 0.2)'
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = '#FFF7ED'
                              e.currentTarget.style.transform = 'translateY(0)'
                              e.currentTarget.style.boxShadow = 'none'
                            }}
                          >
                            <div
                              style={{
                                width: '44px',
                                height: '44px',
                                borderRadius: '12px',
                                backgroundColor: '#EA580C',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                marginBottom: '4px',
                              }}
                            >
                              <BsJournalBookmark size={20} style={{ color: '#ffffff' }} />
                            </div>
                            <span style={{ fontSize: '13px', fontWeight: '600' }}>See Homework</span>
                      </Button>
                    </Col>
                        <Col xs={6} sm={4} md={2.4}>

                      <Button
                            variant="light"
                            onClick={handleViewAdmitCard}
                            className="w-100 action-button"
                            style={{
                              backgroundColor: '#E0F2FE',
                              border: '1px solid #BAE6FD',
                              color: '#0369A1',
                              borderRadius: '10px',
                              padding: '16px 12px',
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              gap: '8px',
                              fontFamily: 'Inter, system-ui, sans-serif',
                              fontWeight: '600',
                              transition: 'all 0.2s ease',
                              minHeight: '100px',
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = '#BAE6FD'
                              e.currentTarget.style.transform = 'translateY(-3px)'
                              e.currentTarget.style.boxShadow = '0 4px 12px rgba(3, 105, 161, 0.2)'
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = '#E0F2FE'
                              e.currentTarget.style.transform = 'translateY(0)'
                              e.currentTarget.style.boxShadow = 'none'
                            }}
                          >
                            <div
                              style={{
                                width: '44px',
                                height: '44px',
                                borderRadius: '12px',
                                backgroundColor: '#0369A1',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                marginBottom: '4px',
                              }}
                            >
                              <FaIdCard size={20} style={{ color: '#ffffff' }} />
                            </div>

                            <span style={{ fontSize: '13px', fontWeight: '600' }}> Admit Card / Exams </span>
                      </Button>

                    </Col>
                    {!fromPassedOutStudents && (
                          <>
                            <Col xs={6} sm={4} md={2.4}>
                        <Button
                                variant="light"
                          onClick={handleDischarge}
                                className="w-100 action-button"
                                style={{
                                  backgroundColor: '#FEE2E2',
                                  border: '1px solid #FECACA',
                                  color: '#EF4444',
                                  borderRadius: '10px',
                                  padding: '16px 12px',
                                  display: 'flex',
                                  flexDirection: 'column',
                                  alignItems: 'center',
                                  gap: '8px',
                                  fontFamily: 'Inter, system-ui, sans-serif',
                                  fontWeight: '600',
                                  transition: 'all 0.2s ease',
                                  minHeight: '100px',
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.backgroundColor = '#FECACA'
                                  e.currentTarget.style.transform = 'translateY(-3px)'
                                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(239, 68, 68, 0.2)'
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.backgroundColor = '#FEE2E2'
                                  e.currentTarget.style.transform = 'translateY(0)'
                                  e.currentTarget.style.boxShadow = 'none'
                                }}
                              >
                                <div
                                  style={{
                                    width: '44px',
                                    height: '44px',
                                    borderRadius: '12px',
                                    backgroundColor: '#EF4444',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    marginBottom: '4px',
                                  }}
                                >
                                  <FaUser size={20} style={{ color: '#ffffff' }} />
                                </div>
                                <span style={{ fontSize: '13px', fontWeight: '600' }}>Move to Leave</span>
                        </Button>
                      </Col>
                            <Col xs={6} sm={4} md={2.4}>
                        <Button
                                variant="light"
                          onClick={handlePassedOut}
                                className="w-100 action-button"
                                style={{
                                  backgroundColor: '#DBEAFE',
                                  border: '1px solid #BFDBFE',
                                  color: '#223843',
                                  borderRadius: '10px',
                                  padding: '16px 12px',
                                  display: 'flex',
                                  flexDirection: 'column',
                                  alignItems: 'center',
                                  gap: '8px',
                                  fontFamily: 'Inter, system-ui, sans-serif',
                                  fontWeight: '600',
                                  transition: 'all 0.2s ease',
                                  minHeight: '100px',
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.backgroundColor = '#BFDBFE'
                                  e.currentTarget.style.transform = 'translateY(-3px)'
                                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(37, 99, 235, 0.2)'
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.backgroundColor = '#DBEAFE'
                                  e.currentTarget.style.transform = 'translateY(0)'
                                  e.currentTarget.style.boxShadow = 'none'
                                }}
                              >
                                <div
                                  style={{
                                    width: '44px',
                                    height: '44px',
                                    borderRadius: '12px',
                                    backgroundColor: '#223843',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    marginBottom: '4px',
                                  }}
                                >
                                  <FaGraduationCap size={20} style={{ color: '#ffffff' }} />
                                </div>
                                <span style={{ fontSize: '13px', fontWeight: '600' }}>Move to Passed Out</span>
                        </Button>
                      </Col>
                          </>
                        )}
                  </Row>
                </Card.Body>
                )}
              </Card>
                </Col>
              </Row>
            </Container>
          </>
        ) : (
          <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '50vh' }}>
            <p style={{ color: '#6b7280', fontSize: '16px', fontFamily: 'Inter, system-ui, sans-serif' }}>
              Loading student data...
            </p>
          </div>
        )}
      </div>

      {/* Leave Student Modal */}
      <Modal
        centered
        show={opentDateModal}
        onHide={() => setOpenDateModal(false)}
      >
        <Modal.Header closeButton 
        closeVariant="white"
        className="bg-danger text-white">
          <Modal.Title>Select Leaving Date</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group>
              <Form.Label>Select Date</Form.Label>
              <Form.Control
                type="date"
                name="leaving_date"
                value={leavingDate}
                onChange={(e) => setLeavingDate(e.target.value)}
              />
            </Form.Group>
          </Form>
          <Modal.Footer>
            <Button
              variant="outline-secondary"
              onClick={() => setOpenDateModal(false)}
            >
              Close
            </Button>
            <Button variant="outline-danger" onClick={handleDischargeNow}>
              Leave Now
            </Button>
          </Modal.Footer>
        </Modal.Body>
      </Modal>

      {/* Passed Out Modal */}
      <Modal
        centered
        size="lg"
        show={openPassedOutModal}
        onHide={resetPassedOutModal}
      >
        <Modal.Header closeButton 
        closeVariant="white"
        className="bg-info text-white">
          <Modal.Title>Select Passed Out Date</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Select Passed Out Date</Form.Label>
              <Form.Control
                type="date"
                name="passed_out_date"
                value={passedOutDate}
                onChange={(e) => setPassedOutDate(e.target.value)}
              />
            </Form.Group>

            <Alert variant="warning" className="mb-3 d-flex align-items-start gap-2 py-3">
              <FaExclamationCircle className="mt-1 flex-shrink-0" style={{ fontSize: '1.1rem' }} />
              <div>
                <div className="fw-semibold mb-1">Important Notice</div>
                <div style={{ fontSize: '0.925rem', lineHeight: 1.5 }}>
                  Once this student is marked as passed out, you will no longer be able to generate
                  Certificates, Marksheets, or Admit Cards for this student.
                </div>
              </div>
            </Alert>

            <Form.Check
              type="checkbox"
              id="passed-out-confirm"
              className="mb-1"
              label="Yes, I'm sure"
              checked={passedOutConfirmed}
              onChange={(e) => setPassedOutConfirmed(e.target.checked)}
            />
          </Form>
          <Modal.Footer>
            <Button
              variant="outline-secondary"
              onClick={resetPassedOutModal}
            >
              Close
            </Button>
            <Button
              variant="info"
              className="text-white"
              onClick={handlePassedOutNow}
              disabled={!passedOutDate || !passedOutConfirmed}
            >
              Mark as Passed Out
            </Button>
          </Modal.Footer>
        </Modal.Body>
      </Modal>

    </>
  )
}

export default ViewStudent
